import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import MarketRecapAdminCard from "@/components/admin/MarketRecapAdminCard";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Loader2, Users, Crown, DollarSign, PieChart, Mail, ThumbsUp, Gift,
  ShieldOff, Download, Trash2, UserPlus, ShieldCheck, Building2, Link2, Copy, Ban,
  BarChart3, Eye, MousePointerClick, CheckCircle2, RefreshCw,
  Inbox, Reply, MessageSquare, Circle,
  KeyRound, ShieldPlus, Lock,
  Activity, ScrollText,
  TrendingUp, Globe, ExternalLink,
  Newspaper, Send,
  ChevronRight, ArrowLeft, LayoutGrid, Upload, FileText, Download as DownloadIcon,
  MessageCircle, RotateCcw, Save, AlertCircle, HelpCircle, PlusCircle as PlusCircleIcon,
  Megaphone, HeartPulse, Radio, UserCog, X, PlayCircle, Share2, Pencil,
} from "lucide-react";
import { parseCsv } from "@/lib/csv";
import usePageSeo from "@/hooks/usePageSeo";
import UserDetailDrawer from "@/components/admin/UserDetailDrawer";
import EmailBroadcastCard from "@/components/admin/EmailBroadcastCard";
import LandingContentCard from "@/components/admin/LandingContentCard";
import AffiliatesAdminCard from "@/components/admin/AffiliatesAdminCard";
import DataUsageCard from "@/components/admin/DataUsageCard";
import { TeamCard } from "@/pages/Pricing";

// This is a client-side gate for the UX; the BACKEND is the real source of truth
// (403s non-admins on every /api/admin/* call). `is_admin` comes from /auth/me.

const fmt = (v) => Number(v ?? 0).toLocaleString();
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  usePageSeo({
    title: "Admin Control Room — Dividend Portfolio Planner",
    description: "Internal DPP admin control room — user grants, trial links, bulk invites, contact inbox, and account tooling.",
    path: "/admin",
    noindex: true,
  });
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [digest, setDigest] = useState(null);
  const [preapproved, setPreapproved] = useState([]);
  const [seatUsage, setSeatUsage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantDays, setGrantDays] = useState(30);
  const [grantNote, setGrantNote] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showAddUser, setShowAddUser] = useState(false);
  const [userDetailId, setUserDetailId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get("section") || "";
  const setSection = (next) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next) nextParams.set("section", next); else nextParams.delete("section");
    setSearchParams(nextParams, { replace: false });
    // Ensure the top of the section is in view when navigating.
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  };

  const loadAll = useCallback(async () => {
    setBusy(true);
    try {
      const [s, u, d, pre, seats] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users", { params: { limit: 50, q: q || undefined } }),
        api.get("/admin/waitlist-digest?days=30"),
        api.get("/admin/preapproved").catch(() => ({ data: { emails: [] } })),
        api.get("/admin/seat-usage").catch(() => ({ data: null })),
      ]);
      setStats(s.data); setUsers(u.data); setDigest(d.data);
      setPreapproved(pre.data?.emails || []);
      setSeatUsage(seats.data);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  }, [q]);

  useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

  if (authLoading) return <div className="p-10 text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.is_admin) {
    return (
      <div className="max-w-md mx-auto px-6 py-20 text-center">
        <div className="w-14 h-14 rounded-full bg-destructive/10 grid place-items-center mx-auto">
          <ShieldOff className="w-6 h-6 text-destructive" />
        </div>
        <h1 className="serif text-3xl mt-4">Admins only</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          You&apos;re signed in as <span className="mono">{user.email}</span>. Ask an admin to add you to the allowlist.
        </p>
      </div>
    );
  }

  const grant = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/admin/grant-pro", { email: grantEmail, days: Number(grantDays), note: grantNote });
      toast.success(`Granted ${res.data.days_added} days to ${res.data.email}`);
      setGrantEmail(""); setGrantNote("");
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Grant failed");
    }
  };

  const revoke = async (email) => {
    if (!window.confirm(`Revoke Pro from ${email}?`)) return;
    try {
      await api.post("/admin/revoke-pro", { email });
      toast.success(`Revoked ${email}`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
  };

  // CSV export — RFC 4180 compliant (quotes wrap fields, embedded quotes doubled).
  const exportCsv = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/users", { params: { limit: 200, q: q || undefined } });
      const rows = res.data;
      if (rows.length === 0) { toast.error("No users to export"); return; }
      const headers = ["email", "name", "plan", "current_period_end", "portfolios_count", "auth_provider", "created_at", "referred_by", "id"];
      const escape = (v) => {
        const s = v == null ? "" : String(v);
        return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
      };
      const csv = [
        headers.join(","),
        ...rows.map((u) => headers.map((h) => escape(u[h])).join(",")),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dpp-users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} user${rows.length === 1 ? "" : "s"} to CSV`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally { setBusy(false); }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === users.length) return new Set();
      return new Set(users.map((u) => u.id));
    });
  };

  const bulkDelete = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (!window.confirm(`Permanently delete ${ids.length} user${ids.length === 1 ? "" : "s"} and ALL their data (portfolios, subscriptions, sessions)? This cannot be undone.`)) return;
    try {
      const res = await api.post("/admin/users/bulk-delete", { ids });
      const skipped = res.data?.skipped_admins || [];
      toast.success(`Deleted ${res.data.deleted} user${res.data.deleted === 1 ? "" : "s"}${skipped.length ? ` — skipped ${skipped.length} admin${skipped.length === 1 ? "" : "s"}` : ""}`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk delete failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div className="min-w-0">
          <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">Admin</div>
          <h1 className="serif text-4xl mt-1">Control Room</h1>
          {section && (
            <button
              type="button"
              onClick={() => setSection("")}
              className="mt-2 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="admin-back-to-hub"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to overview
            </button>
          )}
        </div>
        <Button variant="outline" onClick={loadAll} disabled={busy} className="rounded-full" data-testid="admin-refresh">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Refreshing…</> : "Refresh"}
        </Button>
      </div>

      {/* Stats (always visible) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testId="stat-users" icon={Users} label="Total users" value={fmt(stats?.users_total)} sub={`+${fmt(stats?.users_new_7d)} last 7d`} />
        <StatCard testId="stat-pro" icon={Crown} label="Active Pro" value={fmt(stats?.pro_active)} accent="primary" />
        <StatCard testId="stat-mrr" icon={DollarSign} label="Est. MRR" value={`$${fmt(stats?.mrr_estimate_usd)}`} accent="secondary" sub="Pro count × $9.99" />
        <StatCard testId="stat-portfolios" icon={PieChart} label="Portfolios" value={fmt(stats?.portfolios_total)} />
      </div>

      {/* Section hub — visible when no section is active */}
      {!section && <SectionHub onOpen={setSection} />}

      {/* Users & Grants section */}
      {section === "users" && (<>
      {/* Grant Pro */}
      <Card className="mt-6">
        <CardContent className="p-6">
          <h2 className="serif text-2xl">Grant Pro (enterprise / comp)</h2>
          <p className="text-sm text-muted-foreground mt-1">Extend a user&apos;s Pro subscription by any number of days. Stacks on their existing expiry.</p>
          <form onSubmit={grant} className="mt-4 grid grid-cols-1 md:grid-cols-[1fr,120px,1fr,auto] gap-3 items-end">
            <div>
              <Label htmlFor="ge">User email</Label>
              <Input id="ge" data-testid="grant-email" type="email" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder="user@example.com" required />
            </div>
            <div>
              <Label htmlFor="gd">Days</Label>
              <Input id="gd" data-testid="grant-days" type="number" min={1} max={3650} value={grantDays} onChange={(e) => setGrantDays(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="gn">Note (optional)</Label>
              <Input id="gn" data-testid="grant-note" value={grantNote} onChange={(e) => setGrantNote(e.target.value)} placeholder="e.g. Acme Corp — 5 seats, 6mo pilot" />
            </div>
            <Button type="submit" data-testid="grant-submit" className="rounded-full">
              <Gift className="w-4 h-4 mr-2" /> Grant
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Users */}
      <Card className="mt-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <h2 className="serif text-2xl">Users</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                placeholder="Search email…"
                data-testid="users-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="max-w-sm"
              />
              <Button
                variant="outline"
                onClick={() => setShowAddUser(true)}
                data-testid="add-users-open"
                className="rounded-full shrink-0"
              >
                <UserPlus className="w-4 h-4 mr-1.5" /> Add users
              </Button>
              <Button
                variant="outline"
                onClick={exportCsv}
                disabled={busy}
                data-testid="users-export-csv"
                className="rounded-full shrink-0"
                title="Download the currently-filtered users as CSV"
              >
                <Download className="w-4 h-4 mr-1.5" /> CSV
              </Button>
            </div>
          </div>

          {/* Bulk action bar */}
          {selectedIds.size > 0 && (
            <div
              data-testid="bulk-toolbar"
              className="flex items-center justify-between rounded-md border border-destructive/30 bg-destructive/5 px-4 py-2.5 mb-3"
            >
              <div className="text-sm">
                <span className="mono text-destructive font-semibold">{selectedIds.size}</span> selected
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost" size="sm"
                  onClick={() => setSelectedIds(new Set())}
                  data-testid="bulk-clear"
                >
                  Clear
                </Button>
                <Button
                  variant="destructive" size="sm"
                  onClick={bulkDelete}
                  data-testid="bulk-delete"
                  className="rounded-full"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete {selectedIds.size}
                </Button>
              </div>
            </div>
          )}

          <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/60">
                <tr className="text-left">
                  <th className="px-3 py-2 w-8">
                    <Checkbox
                      data-testid="select-all"
                      checked={users.length > 0 && selectedIds.size === users.length}
                      onCheckedChange={toggleSelectAll}
                      aria-label="Select all"
                    />
                  </th>
                  <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Email</th>
                  <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Plan</th>
                  <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Expires</th>
                  <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Portfolios</th>
                  <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Joined</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody data-testid="users-table">
                {users.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No users match</td></tr>
                )}
                {users.map((u) => (
                  <tr key={u.id} className={`border-t border-border ${selectedIds.has(u.id) ? "bg-destructive/5" : ""}`}>
                    <td className="px-3 py-2">
                      <Checkbox
                        data-testid={`select-user-${u.id}`}
                        checked={selectedIds.has(u.id)}
                        onCheckedChange={() => toggleSelect(u.id)}
                        aria-label={`Select ${u.email}`}
                      />
                    </td>
                    <td className="px-3 py-2 mono text-xs">{u.email}</td>
                    <td className="px-3 py-2">
                      <Badge variant={u.plan === "pro" ? "default" : "outline"} className={`mono text-[10px] uppercase ${u.plan === "pro" ? "bg-primary text-primary-foreground" : ""}`}>
                        {u.plan}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 mono text-xs text-muted-foreground">{fmtDate(u.current_period_end)}</td>
                    <td className="px-3 py-2 mono text-xs text-right">{u.portfolios_count}</td>
                    <td className="px-3 py-2 mono text-xs text-muted-foreground">{fmtDate(u.created_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="ghost" size="sm"
                        data-testid={`user-view-${u.id}`}
                        onClick={() => setUserDetailId(u.id)}
                      >
                        <Eye className="w-3.5 h-3.5 mr-1" /> View
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => { setGrantEmail(u.email); document.getElementById("ge")?.focus(); }}
                      >
                        <Gift className="w-3.5 h-3.5 mr-1" /> Grant
                      </Button>
                      {u.plan === "pro" && (
                        <Button variant="ghost" size="sm" onClick={() => revoke(u.email)}>
                          <ShieldOff className="w-3.5 h-3.5 mr-1" /> Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Seat usage (Team tier) */}
      {seatUsage && (
        <Card className="mt-6" data-testid="seat-usage-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-4 h-4 text-primary" />
              <h2 className="serif text-2xl">Seat usage · Team tier</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Consumption of Team-tier pre-approved seats grouped by company (the <span className="mono">note</span> you set when adding users).
              Reorder before <span className="mono">Utilization</span> hits 90%.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <StatCard testId="seat-issued"  icon={Users}     label="Seats issued"   value={fmt(seatUsage.totals.seats_issued)} />
              <StatCard testId="seat-consumed" icon={ShieldCheck} label="Consumed"      value={fmt(seatUsage.totals.consumed)} accent="primary" />
              <StatCard testId="seat-pending"  icon={Gift}      label="Pending"        value={fmt(seatUsage.totals.pending)} accent="secondary" />
              <StatCard testId="seat-util"     icon={PieChart}  label="Utilization"    value={`${seatUsage.totals.utilization_pct}%`} sub={`${seatUsage.totals.organic_pro_subs} organic Pro`} />
            </div>

            {/* Company breakdown */}
            {seatUsage.by_company.length === 0 ? (
              <div className="text-sm text-muted-foreground italic">
                No pre-approvals yet. Add company seats via <span className="mono">Add users</span> above and set a <span className="mono">note</span> for grouping.
              </div>
            ) : (
              <div className="overflow-hidden rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60">
                    <tr className="text-left">
                      <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Company / note</th>
                      <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Consumed</th>
                      <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Pending</th>
                      <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Total</th>
                      <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground w-40">Utilization</th>
                    </tr>
                  </thead>
                  <tbody data-testid="seat-usage-companies">
                    {seatUsage.by_company.map((c) => {
                      const util = c.total ? (c.consumed / c.total) * 100 : 0;
                      const warn = util >= 90;
                      return (
                        <tr key={c.note} className="border-t border-border">
                          <td className="px-3 py-2">{c.note}</td>
                          <td className="px-3 py-2 mono text-xs text-right text-primary">{c.consumed}</td>
                          <td className="px-3 py-2 mono text-xs text-right text-secondary">{c.pending}</td>
                          <td className="px-3 py-2 mono text-xs text-right">{c.total}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                                <div className={`h-full ${warn ? "bg-destructive" : util >= 60 ? "bg-secondary" : "bg-primary"}`} style={{ width: `${Math.max(2, util)}%` }} />
                              </div>
                              <span className={`mono text-[10px] ${warn ? "text-destructive font-semibold" : "text-muted-foreground"}`}>
                                {util.toFixed(0)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pre-approved list */}
      <Card className="mt-6">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <h2 className="serif text-2xl">Pre-approved (Google sign-up)</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            When these emails sign in with Google for the first time, they automatically receive the granted Pro days.
          </p>
          {preapproved.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">No pre-approved emails yet. Click <span className="mono">Add users</span> above to invite enterprise accounts.</div>
          ) : (
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/60">
                  <tr className="text-left">
                    <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Email</th>
                    <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Days</th>
                    <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Status</th>
                    <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Added</th>
                    <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Note</th>
                  </tr>
                </thead>
                <tbody data-testid="preapproved-table">
                  {preapproved.map((p) => (
                    <tr key={p.email} className="border-t border-border">
                      <td className="px-3 py-2 mono text-xs">{p.email}</td>
                      <td className="px-3 py-2 mono text-xs">{p.days || 0}</td>
                      <td className="px-3 py-2">
                        {p.consumed_at ? (
                          <Badge variant="outline" className="mono text-[10px] uppercase">Redeemed</Badge>
                        ) : (
                          <Badge className="bg-secondary text-secondary-foreground mono text-[10px] uppercase">Pending</Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 mono text-xs text-muted-foreground">{fmtDate(p.added_at)}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-xs">{p.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Waitlist + Roadmap votes side-by-side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-secondary" />
              <h2 className="serif text-xl">Waitlist (30 days)</h2>
            </div>
            <div className="mono text-3xl mt-2 serif">{fmt(digest?.digest?.total_signups)}</div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">total signups</div>
            <div className="mt-4 space-y-2">
              {(digest?.digest?.by_feature || []).slice(0, 6).map((r) => (
                <div key={r.feature} className="flex items-center justify-between text-sm">
                  <span className="text-foreground/80 truncate mr-2">{r.feature}</span>
                  <span className="mono text-secondary">{r.count}</span>
                </div>
              ))}
              {(!digest?.digest?.by_feature || digest.digest.by_feature.length === 0) && (
                <div className="text-sm text-muted-foreground italic">No signups in this window</div>
              )}
            </div>
            <div className="mono text-[10px] text-muted-foreground mt-4">
              Resend: {digest?.resend_configured ? "configured ✓" : "not configured — endpoint returns JSON only"}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2">
              <ThumbsUp className="w-4 h-4 text-primary" />
              <h2 className="serif text-xl">Roadmap votes</h2>
            </div>
            <div className="mt-4 space-y-2">
              {(stats?.roadmap_votes || []).map((v) => (
                <div key={v.feature_key} className="flex items-center justify-between text-sm">
                  <span className="mono text-foreground/80">{v.feature_key}</span>
                  <span className="mono text-primary font-semibold">{v.count}</span>
                </div>
              ))}
              {(!stats?.roadmap_votes || stats.roadmap_votes.length === 0) && (
                <div className="text-sm text-muted-foreground italic">No votes yet</div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8 text-xs text-muted-foreground text-center">
        Admin lane · Backend is the source of truth (email allowlist enforced server-side).
      </div>
      </>)}

      {section === "trial-links" && <TrialLinksCard />}

      {section === "admin-accounts" && <AdminAccountsCard />}

      {section === "traffic" && <TrafficCard />}

      {section === "activity" && <ActivityLogCard />}

      {section === "newsletter" && <NewsletterCard />}
      {section === "market-recap" && <MarketRecapAdminCard />}

      {section === "contact" && <ContactInboxCard />}

      {section === "trust" && <TrustCenterAdminCard />}

      {section === "chatbot" && <ChatbotAdminCard />}

      {section === "faq" && <FaqAdminCard />}
      {section === "team-pricing" && <TeamPricingAdminCard user={user} />}
      {section === "broker-diagnostics" && <SnapTradeDiagnosticsCard />}

      {section === "announcements"  && <AnnouncementsAdminCard />}
      {section === "push-broadcast" && <PushBroadcastCard />}
      {section === "email-broadcast"&& <EmailBroadcastCard />}
      {section === "feature-flags"  && <FeatureFlagsAdminCard />}
      {section === "landing-content" && <LandingContentCard />}
      {section === "affiliates"     && <AffiliatesAdminCard />}
      {section === "health"         && <HealthDashboardCard />}
      {section === "data-usage"     && <DataUsageCard />}

      <UserDetailDrawer
        userId={userDetailId}
        onClose={() => setUserDetailId(null)}
        onChanged={loadAll}
      />

      <AddUsersDialog
        open={showAddUser}
        onOpenChange={setShowAddUser}
        onDone={loadAll}
      />
    </div>
  );
}

// ---------- Section Hub — tile grid that replaces the long stacked-cards layout ----------
const SECTIONS = [
  { key: "users",          icon: Users,       title: "Users & Grants",     blurb: "Search users, drill into any account, grant/revoke Pro, seat usage, waitlist + roadmap votes." },
  { key: "announcements",  icon: Megaphone,   title: "Site announcements", blurb: "Create dismissible banners for the whole site — new features, maintenance, launches. Optionally send as push + email in one shot." },
  { key: "push-broadcast", icon: Radio,       title: "Push broadcast",     blurb: "Send a Web Push to all users, Pro-only, Free-only, or a single account. Great for launches + outages." },
  { key: "email-broadcast",icon: Mail,        title: "Email broadcast",    blurb: "Send a segmented HTML email via Resend. Respects newsletter opt-outs except when picking a single user." },
  { key: "landing-content", icon: PlayCircle,  title: "Landing content",   blurb: "Swap the hero video and other editable copy on the marketing landing page." },
  { key: "affiliates",     icon: Share2,      title: "Affiliates & Influencers", blurb: "Custom tracking links for influencers, endorsers, channel partners. Auto-calculated commission per subscription + payout log." },
  { key: "feature-flags",  icon: KeyRound,    title: "Feature flags",      blurb: "Toggle beta features globally or grant them to specific users. Frontend gates read from /api/feature-flags." },
  { key: "data-usage",     icon: BarChart3,   title: "Market data usage",  blurb: "Today's FMP lookups against the daily limit, plus which tickers fell back to Yahoo Finance and why." },
  { key: "health",         icon: HeartPulse,  title: "Integration health", blurb: "Live green/red for SnapTrade, Stripe, Resend, Twilio, Emergent LLM, Web Push, DB, and Fernet encryption." },
  { key: "trial-links",    icon: Gift,        title: "Trial links",        blurb: "Create shareable Pro-trial URLs, bulk email invites, PWA install invites, funnel analytics." },
  { key: "admin-accounts", icon: ShieldCheck, title: "Admin accounts",     blurb: "Add or revoke admin access. Env allowlist admins are immutable and shown as locked." },
  { key: "newsletter",     icon: Newspaper,   title: "Weekly newsletter",  blurb: "Preview the Saturday digest, personalize by subscriber, send now, view subscribers." },
  { key: "market-recap",   icon: Newspaper,   title: "Market Recap posts", blurb: "Compose ad-hoc dividend headlines, breaking news, and edit the auto-generated Saturday weekly recaps." },
  { key: "traffic",        icon: TrendingUp,  title: "Site traffic",       blurb: "Pageviews, unique visitors, top pages, top referrers over 24h / 7d / 30d." },
  { key: "activity",       icon: Activity,    title: "Activity log",       blurb: "Audit trail of every admin mutation — filterable by actor, action, target." },
  { key: "contact",        icon: Inbox,       title: "Contact inbox",      blurb: "Submissions from the /contact form. Reply via mailto, add notes, mark resolved." },
  { key: "trust",          icon: ShieldCheck, title: "Trust Center",       blurb: "Upload the current PDF for each policy (Terms, Privacy, Cookie…). New versions trigger re-acceptance for every user." },
  { key: "chatbot",        icon: MessageCircle, title: "Chatbot",          blurb: "Volume · escalation rate · top questions · edit the KB the bot answers from." },
  { key: "faq",            icon: HelpCircle,  title: "Public FAQ",         blurb: "Add, edit, and reorder the FAQs on /faq. Categories: Features · Value · Sign-up." },
  { key: "team-pricing",   icon: Building2,   title: "Team pricing",       blurb: "Internal seat-based Team tier — per-seat estimate, contact-sales lead capture, and perks summary. Moved off the public pricing page (2026-02-13)." },
  { key: "broker-diagnostics", icon: HeartPulse, title: "SnapTrade diagnostics",  blurb: "Live self-test of the deployed SnapTrade config — API keys, encryption key, and a real /api-status ping so you can see if the broker-sync integration is healthy without opening a browser tab against SnapTrade." },
];

function SectionHub({ onOpen }) {
  return (
    <div className="mt-8" data-testid="admin-section-hub">
      <div className="flex items-center gap-2 mb-4 text-muted-foreground">
        <LayoutGrid className="w-3.5 h-3.5" />
        <span className="mono text-[10px] uppercase tracking-widest">Sections · tap to open</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => onOpen(s.key)}
            className="group text-left rounded-lg border border-border bg-card hover:border-primary/40 hover:bg-muted/30 transition-colors p-5 focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid={`admin-section-tile-${s.key}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="w-9 h-9 rounded-md bg-primary/10 grid place-items-center text-primary">
                <s.icon className="w-4 h-4" />
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
            </div>
            <div className="serif text-xl mt-3">{s.title}</div>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{s.blurb}</p>
          </button>
        ))}
      </div>
    </div>
  );
}



function StatCard({ label, value, icon: Icon, accent, sub, testId }) {
  const accentCls = accent === "primary" ? "text-primary" : accent === "secondary" ? "text-secondary" : "text-foreground";
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Icon className="w-4 h-4" />
          <span className="mono text-[10px] uppercase tracking-widest">{label}</span>
        </div>
        <div className={`serif text-2xl mt-2 ${accentCls}`}>{value}</div>
        {sub && <div className="mono text-[10px] text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

// ---------- Add Users Dialog (manual + CSV upload) ----------

const EMAIL_RE = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;

function AddUsersDialog({ open, onOpenChange, onDone }) {
  const [tab, setTab] = useState("manual");
  const [manualText, setManualText] = useState("");
  const [days, setDays] = useState(30);
  const [note, setNote] = useState("");
  const [csvParsed, setCsvParsed] = useState([]); // [{email, name?}]
  const [csvError, setCsvError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setManualText(""); setDays(30); setNote("");
      setCsvParsed([]); setCsvError(""); setTab("manual");
    }
  }, [open]);

  // Parse manual textarea: one email per line OR "email,name" per line.
  const manualEmails = useMemo(() => {
    const out = [];
    for (const raw of manualText.split(/[\n;]/)) {
      const parts = raw.split(",");
      const email = (parts[0] || "").trim().toLowerCase();
      if (!email) continue;
      if (EMAIL_RE.test(email)) out.push(email);
    }
    return [...new Set(out)];
  }, [manualText]);

  const onCsvFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCsvError("");
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length === 0) { setCsvError("Empty file"); return; }
      // Detect header row
      const first = rows[0].map((c) => c.trim().toLowerCase());
      let startRow = 0;
      let emailIdx = 0;
      const nameIdx = first.indexOf("name");
      if (first.includes("email")) {
        emailIdx = first.indexOf("email");
        startRow = 1;
      }
      const parsed = [];
      const seen = new Set();
      for (let i = startRow; i < rows.length; i++) {
        const row = rows[i];
        const email = (row[emailIdx] || "").trim().toLowerCase();
        if (!email || seen.has(email)) continue;
        if (!EMAIL_RE.test(email)) continue;
        const name = nameIdx >= 0 ? (row[nameIdx] || "").trim() : "";
        parsed.push({ email, name });
        seen.add(email);
      }
      if (parsed.length === 0) {
        setCsvError("No valid emails found. Expected column: `email` (optional `name`).");
        return;
      }
      setCsvParsed(parsed);
    } catch (err) {
      setCsvError(`Could not parse file: ${err.message || err}`);
    }
  };

  const emailsToSubmit = tab === "manual" ? manualEmails : csvParsed.map((r) => r.email);

  const submit = async () => {
    if (emailsToSubmit.length === 0) { toast.error("Add at least one email"); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/admin/preapprove", {
        emails: emailsToSubmit,
        days: Number(days),
        note,
      });
      toast.success(`Pre-approved ${res.data.total_updated} email${res.data.total_updated === 1 ? "" : "s"} (${res.data.added} new)`);
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="add-users-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">Add users (Google pre-approval)</DialogTitle>
          <DialogDescription>
            Pre-approve emails for Google sign-in. When they log in with Google for the first time,
            they&apos;ll automatically get the granted Pro days. Use <span className="mono">days = 0</span> for free-tier invites.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="manual" data-testid="tab-manual">Manual entry</TabsTrigger>
            <TabsTrigger value="csv" data-testid="tab-csv">CSV upload</TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="mt-4 space-y-3">
            <div>
              <Label htmlFor="manual-emails">Emails (one per line, or <span className="mono">email,name</span>)</Label>
              <Textarea
                id="manual-emails"
                data-testid="manual-emails"
                rows={6}
                placeholder={"alice@acme.com\nbob@acme.com,Bob Smith\ncarol@acme.com"}
                value={manualText}
                onChange={(e) => setManualText(e.target.value)}
                className="mono text-sm"
              />
              <div className="mono text-[11px] text-muted-foreground mt-1">
                {manualEmails.length} valid email{manualEmails.length === 1 ? "" : "s"} detected
              </div>
            </div>
          </TabsContent>

          <TabsContent value="csv" className="mt-4 space-y-3">
            <div>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={onCsvFile}
                data-testid="csv-file-input"
              />
              <Button
                variant="outline"
                onClick={() => fileRef.current?.click()}
                className="rounded-full"
                data-testid="csv-upload-btn"
              >
                <Download className="w-4 h-4 mr-2 rotate-180" /> Choose CSV
              </Button>
              <span className="ml-3 mono text-[11px] text-muted-foreground">
                Format: <span className="mono">email,name</span> · header optional
              </span>
            </div>
            {csvError && <div className="text-sm text-destructive" data-testid="csv-error">{csvError}</div>}
            {csvParsed.length > 0 && (
              <div className="rounded-md border border-border overflow-hidden max-h-56 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 sticky top-0">
                    <tr className="text-left">
                      <th className="px-3 py-1.5 mono text-[10px] uppercase tracking-widest text-muted-foreground">Email</th>
                      <th className="px-3 py-1.5 mono text-[10px] uppercase tracking-widest text-muted-foreground">Name</th>
                    </tr>
                  </thead>
                  <tbody data-testid="csv-preview">
                    {csvParsed.map((r) => (
                      <tr key={r.email} className="border-t border-border">
                        <td className="px-3 py-1.5 mono text-xs">{r.email}</td>
                        <td className="px-3 py-1.5 text-xs text-muted-foreground">{r.name || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mono text-[11px] text-muted-foreground">
              {csvParsed.length} email{csvParsed.length === 1 ? "" : "s"} ready to import
            </div>
          </TabsContent>
        </Tabs>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label htmlFor="pre-days">Pro days (0 = free-tier invite)</Label>
            <Input
              id="pre-days"
              data-testid="preapprove-days"
              type="number" min={0} max={3650}
              value={days}
              onChange={(e) => setDays(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="pre-note">Note (optional)</Label>
            <Input
              id="pre-note"
              data-testid="preapprove-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Acme Corp enterprise seats"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="add-users-cancel">Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || emailsToSubmit.length === 0}
            className="rounded-full"
            data-testid="add-users-submit"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding…</> :
              <><UserPlus className="w-4 h-4 mr-2" /> Add {emailsToSubmit.length || ""}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Pro Trial Links ----------
function TrialLinksCard() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);
  const [maxUses, setMaxUses] = useState(1);
  const [expiresIn, setExpiresIn] = useState(30);
  const [note, setNote] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [creating, setCreating] = useState(false);
  const [analyticsBump, setAnalyticsBump] = useState(0);

  const load = async () => {
    try {
      const r = await api.get("/admin/trial-links");
      setLinks(r.data.links || []);
    } catch { /* silent — admin may not have any yet */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const r = await api.post("/admin/trial-links", {
        days: Number(days),
        max_uses: Number(maxUses),
        expires_in_days: Number(expiresIn),
        note: note.trim() || undefined,
        email: emailTo.trim() || undefined,
        app_url: window.location.origin,
      });
      const url = `${window.location.origin}/trial/${r.data.token}`;
      try { await navigator.clipboard.writeText(url); } catch { /* clipboard may be blocked */ }
      if (r.data.emailed_to) {
        toast.success(`Link emailed to ${r.data.emailed_to} · also copied to clipboard`);
      } else {
        toast.success(`Link copied · ${days} days of Pro`);
      }
      setNote("");
      setEmailTo("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create link");
    } finally { setCreating(false); }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/trial/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch { toast.error("Copy failed — select the URL manually"); }
  };

  const emailExisting = async (token) => {
    const to = window.prompt("Email this trial link to which address?");
    if (!to || !to.trim()) return;
    try {
      const r = await api.post(`/admin/trial-links/${token}/email`, {
        email: to.trim(),
        app_url: window.location.origin,
      });
      toast.success(`Link sent to ${r.data.emailed_to}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Send failed"); }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this trial link? Anyone still holding it won't be able to redeem.")) return;
    try {
      await api.post(`/admin/trial-links/${token}/revoke`);
      toast.success("Link revoked");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Revoke failed"); }
  };

  const remove = async (token) => {
    if (!window.confirm("Delete this trial link permanently?")) return;
    try {
      await api.delete(`/admin/trial-links/${token}`);
      toast.success("Link deleted");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <Card className="mt-6" data-testid="trial-links-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <Link2 className="w-4 h-4 text-secondary" />
          <h2 className="serif text-2xl">Pro trial links</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Generate a shareable link that grants N days of Pro when the recipient signs in. Pick any duration up to 365 days. Perfect for beta invites, podcast promos, and comps.
        </p>

        <form onSubmit={create} className="mt-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-[120px,120px,140px,1fr] gap-3 items-end">
            <div>
              <Label htmlFor="tl-days">Days of Pro</Label>
              <Input id="tl-days" type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} required data-testid="trial-days" />
            </div>
            <div>
              <Label htmlFor="tl-uses">Max uses</Label>
              <Input id="tl-uses" type="number" min={1} max={1000} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} required data-testid="trial-max-uses" />
            </div>
            <div>
              <Label htmlFor="tl-exp">Link expires in</Label>
              <div className="relative">
                <Input id="tl-exp" type="number" min={1} max={365} value={expiresIn} onChange={(e) => setExpiresIn(e.target.value)} required className="pr-14" data-testid="trial-expires" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 mono text-xs text-muted-foreground pointer-events-none">days</span>
              </div>
            </div>
            <div>
              <Label htmlFor="tl-note">Note (optional)</Label>
              <Input id="tl-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Bogleheads podcast · Jan cohort" data-testid="trial-note" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr,auto] gap-3 items-end">
            <div>
              <Label htmlFor="tl-email">Email link to (optional — leaves clipboard copy either way)</Label>
              <Input id="tl-email" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="podcaster@example.com" data-testid="trial-email-to" />
            </div>
            <Button type="submit" disabled={creating} className="rounded-full" data-testid="trial-create">
              {creating
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</>
                : <><Link2 className="w-4 h-4 mr-2" /> {emailTo.trim() ? "Create & email" : "Create & copy"}</>}
            </Button>
          </div>
        </form>

        {/* Bulk invite */}
        <BulkInviteBlock days={days} maxUses={maxUses} expiresIn={expiresIn} note={note} onSent={() => { load(); setAnalyticsBump((x) => x + 1); }} />

        {/* Install-link outreach — sends the "Add DPP to your home screen" invite */}
        <InstallInviteBlock />

        {/* Campaign analytics (only appears when at least one link exists) */}
        <BulkInviteAnalytics visible={links.length > 0} refreshSignal={analyticsBump} />

        <div className="mt-6">
          {loading ? (
            <div className="h-20 rounded-md bg-muted animate-pulse" />
          ) : links.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6 border border-dashed rounded-md">
              No trial links yet. Create one above and share the URL.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm" data-testid="trial-links-table">
                <thead className="bg-muted/60">
                  <tr className="text-left">
                    <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Link</th>
                    <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Days</th>
                    <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Uses</th>
                    <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Expires</th>
                    <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {links.map((l) => {
                    const consumed = l.uses >= l.max_uses;
                    const expired = l.expires_at && new Date(l.expires_at) < new Date();
                    const status = l.revoked ? "revoked" : expired ? "expired" : consumed ? "consumed" : "active";
                    const url = `${window.location.origin}/trial/${l.token}`;
                    return (
                      <tr key={l.token} className="border-t border-border" data-testid={`trial-row-${l.token}`}>
                        <td className="px-3 py-2 min-w-0">
                          <div className="mono text-[11px] text-foreground/80 truncate max-w-[280px]" title={url}>
                            /trial/{l.token.slice(0, 10)}…
                          </div>
                          {l.note && <div className="text-[11px] text-muted-foreground italic mt-0.5">&ldquo;{l.note}&rdquo;</div>}
                        </td>
                        <td className="px-3 py-2 mono text-right">{l.days}</td>
                        <td className="px-3 py-2 mono text-right">
                          {l.uses}<span className="text-muted-foreground">/{l.max_uses}</span>
                        </td>
                        <td className="px-3 py-2 mono text-[11px] text-muted-foreground whitespace-nowrap">
                          {fmtDate(l.expires_at)}
                        </td>
                        <td className="px-3 py-2">
                          <Badge
                            variant="outline"
                            className={
                              status === "active" ? "border-primary/40 text-primary" :
                              status === "consumed" ? "border-secondary/40 text-secondary" :
                              "border-destructive/40 text-destructive"
                            }
                          >
                            {status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button
                            onClick={() => copyLink(l.token)}
                            className="p-1.5 rounded text-muted-foreground hover:text-primary hover:bg-primary/10"
                            title="Copy full URL"
                            data-testid={`trial-copy-${l.token}`}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                          {!l.revoked && !consumed && (
                            <button
                              onClick={() => emailExisting(l.token)}
                              className="p-1.5 rounded text-muted-foreground hover:text-primary hover:bg-primary/10"
                              title="Email this link"
                              data-testid={`trial-email-${l.token}`}
                            >
                              <Mail className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {!l.revoked && !consumed && (
                            <button
                              onClick={() => revoke(l.token)}
                              className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                              title="Revoke"
                              data-testid={`trial-revoke-${l.token}`}
                            >
                              <Ban className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => remove(l.token)}
                            className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            title="Delete permanently"
                            data-testid={`trial-delete-${l.token}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- Bulk trial invite (CSV upload OR typed emails) ----------
function parseEmails(text) {
  const out = [];
  const seen = new Set();
  const bad = [];
  for (const raw of String(text || "").split(/[\n,;\t\s]+/)) {
    const e = raw.trim().toLowerCase();
    if (!e) continue;
    if (!EMAIL_RE.test(e)) { bad.push(e); continue; }
    if (seen.has(e)) continue;
    seen.add(e);
    out.push(e);
  }
  return { emails: out, bad };
}

// Extracts every valid email address from an arbitrary blob of text —
// Google Contacts CSV (quoted fields, ` ::: ` separator between multiple
// emails on the same contact, MailingLabel columns), vCard (`EMAIL;TYPE=…:`),
// and plain lists all parse cleanly. Dedupes case-insensitively and returns
// the discovery count so the paste UX can echo "found 42 emails".
function extractEmailsFromBlob(text) {
  const found = String(text || "").match(/[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g) || [];
  const seen = new Set();
  const out = [];
  for (const f of found) {
    const e = f.toLowerCase();
    if (seen.has(e)) continue;
    seen.add(e);
    out.push(e);
  }
  return out;
}

function BulkInviteBlock({ days, maxUses, expiresIn, note, onSent }) {
  const [mode, setMode] = useState("type"); // 'type' | 'csv' | 'gcontacts'
  const [text, setText] = useState("");
  const [bad, setBad] = useState([]);
  const [sending, setSending] = useState(false);
  const [summary, setSummary] = useState(null);
  const [gcRaw, setGcRaw] = useState("");     // raw pasted CSV/vCard
  const [gcExtracted, setGcExtracted] = useState(0); // last extract count

  const { emails } = parseEmails(text);

  const handleFile = async (file) => {
    if (!file) return;
    const raw = await file.text();
    setText(raw);
    const { bad: b } = parseEmails(raw);
    setBad(b);
    toast.success(`Loaded ${file.name} · ${parseEmails(raw).emails.length} valid emails`);
  };

  // Google Contacts helper — user pastes the CSV export (File → Download →
  // Google CSV) or vCard, we regex-extract every email, merge with any
  // existing entries in the primary textarea, and flip the mode back to
  // "type" so the admin sees the resulting list + valid count before sending.
  const extractGoogleContacts = () => {
    const extracted = extractEmailsFromBlob(gcRaw);
    setGcExtracted(extracted.length);
    if (extracted.length === 0) {
      toast.error("No emails found in the pasted content");
      return;
    }
    // Merge with anything already typed — dedupe against the existing set.
    const existing = new Set(parseEmails(text).emails);
    const additions = extracted.filter((e) => !existing.has(e));
    const merged = [...parseEmails(text).emails, ...additions].join("\n");
    setText(merged);
    setBad([]);
    setMode("type");
    toast.success(
      additions.length === extracted.length
        ? `Loaded ${extracted.length} email${extracted.length === 1 ? "" : "s"} from Google Contacts`
        : `Loaded ${additions.length} new email${additions.length === 1 ? "" : "s"} · ${extracted.length - additions.length} already present`
    );
  };

  const send = async (e) => {
    e.preventDefault();
    if (emails.length === 0) { toast.error("No valid emails to send"); return; }
    if (!window.confirm(`Send ${emails.length} trial invite${emails.length === 1 ? "" : "s"}? Each gets ${days} days of Pro.`)) return;
    setSending(true); setSummary(null);
    try {
      const r = await api.post("/admin/trial-links/bulk", {
        emails,
        days: Number(days),
        max_uses: Number(maxUses),
        expires_in_days: Number(expiresIn),
        note: note.trim() || undefined,
        app_url: window.location.origin,
      });
      setSummary(r.data);
      toast.success(`Sent ${r.data.sent}/${r.data.total} invites`);
      onSent && onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk send failed");
    } finally { setSending(false); }
  };

  return (
    <div className="mt-6 pt-5 border-t border-border" data-testid="bulk-invite-block">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-secondary flex items-center gap-1.5">
            <UserPlus className="w-3 h-3" /> Bulk invite
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            Send one trial invite per recipient — settings from the form above (days, note, expiry) apply to every invite.
          </div>
        </div>
        <div className="inline-flex rounded-full border border-border overflow-hidden mono text-[10px] uppercase tracking-widest">
          <button
            type="button"
            onClick={() => setMode("type")}
            className={`px-3 py-1.5 ${mode === "type" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}
            data-testid="bulk-mode-type"
          >Type</button>
          <button
            type="button"
            onClick={() => setMode("csv")}
            className={`px-3 py-1.5 ${mode === "csv" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}
            data-testid="bulk-mode-csv"
          >CSV upload</button>
          <button
            type="button"
            onClick={() => setMode("gcontacts")}
            className={`px-3 py-1.5 ${mode === "gcontacts" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}
            data-testid="bulk-mode-gcontacts"
            title="Paste a Google Contacts CSV or vCard — we'll auto-extract every email"
          >Google Contacts</button>
        </div>
      </div>

      <form onSubmit={send} className="space-y-3">
        {mode === "type" ? (
          <div>
            <Label htmlFor="bulk-emails">Paste or type emails (one per line, or comma-separated)</Label>
            <textarea
              id="bulk-emails"
              value={text}
              onChange={(e) => { setText(e.target.value); setBad(parseEmails(e.target.value).bad); }}
              rows={5}
              placeholder="alice@example.com&#10;bob@example.com&#10;carol@example.com"
              className="w-full mt-1 rounded-md border border-border bg-background px-3 py-2 text-sm mono focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="bulk-textarea"
            />
          </div>
        ) : mode === "csv" ? (
          <div>
            <Label htmlFor="bulk-file">Upload CSV (first column = email; header row auto-detected)</Label>
            <Input
              id="bulk-file"
              type="file"
              accept=".csv,.txt"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="bulk-file"
            />
            {text && (
              <details className="mt-2">
                <summary className="mono text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer">Preview parsed content</summary>
                <pre className="mt-1 text-[11px] mono bg-muted/40 rounded p-2 max-h-32 overflow-auto">{text.slice(0, 800)}{text.length > 800 ? "…" : ""}</pre>
              </details>
            )}
          </div>
        ) : (
          <div className="space-y-2" data-testid="bulk-gcontacts-panel">
            <Label htmlFor="bulk-gcontacts-raw">Paste your Google Contacts export</Label>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              In Google Contacts, click <span className="mono">Export → Google CSV</span> (or vCard), open the file, and paste
              the entire contents below. We&apos;ll extract every valid email — names, phone numbers, and other columns are
              ignored. Extracted addresses merge into your invite list, deduped against anything already typed.
            </p>
            <textarea
              id="bulk-gcontacts-raw"
              value={gcRaw}
              onChange={(e) => setGcRaw(e.target.value)}
              rows={6}
              placeholder='Name,Given Name,Family Name,E-mail 1 - Value,…&#10;Ada Lovelace,Ada,Lovelace,ada@analyticalengine.co,…&#10;Grace Hopper,Grace,Hopper,grace@navy.mil ::: grace.hopper@yale.edu,…'
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-[11px] mono focus:outline-none focus:ring-2 focus:ring-primary/30"
              data-testid="bulk-gcontacts-textarea"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                onClick={extractGoogleContacts}
                disabled={!gcRaw.trim()}
                variant="secondary"
                className="rounded-full"
                data-testid="bulk-gcontacts-extract"
              >
                <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Extract emails
              </Button>
              {gcExtracted > 0 && (
                <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="bulk-gcontacts-count">
                  Last extract: {gcExtracted} email{gcExtracted === 1 ? "" : "s"}
                </span>
              )}
            </div>
            {text && (
              <details className="mt-1">
                <summary className="mono text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer">Current invite list ({emails.length})</summary>
                <pre className="mt-1 text-[11px] mono bg-muted/40 rounded p-2 max-h-32 overflow-auto">{text.slice(0, 800)}{text.length > 800 ? "…" : ""}</pre>
              </details>
            )}
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs">
            <span className="mono uppercase tracking-widest text-muted-foreground">Ready:</span>{" "}
            <span className="mono font-semibold text-primary" data-testid="bulk-valid-count">{emails.length}</span>{" "}
            <span className="text-muted-foreground">valid</span>
            {bad.length > 0 && (
              <span className="ml-2 text-destructive mono">· {bad.length} skipped</span>
            )}
          </div>
          <Button
            type="submit"
            disabled={sending || emails.length === 0}
            className="rounded-full"
            data-testid="bulk-send"
          >
            {sending
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending {emails.length}…</>
              : <><Mail className="w-4 h-4 mr-2" /> Send {emails.length || ""} invite{emails.length === 1 ? "" : "s"}</>}
          </Button>
        </div>

        {bad.length > 0 && (
          <div className="text-[11px] text-destructive/80" data-testid="bulk-skipped">
            Skipped (not valid emails): {bad.slice(0, 5).join(", ")}{bad.length > 5 ? ` +${bad.length - 5} more` : ""}
          </div>
        )}

        {summary && (
          <div className={`rounded-md border p-3 ${summary.failed === 0 ? "border-primary/30 bg-primary/[0.04]" : summary.sent === 0 ? "border-destructive/40 bg-destructive/[0.04]" : "border-secondary/30 bg-secondary/[0.05]"}`} data-testid="bulk-summary">
            <div className="text-sm">
              {summary.sent === 0
                ? <><strong className="text-destructive">All {summary.total} invite{summary.total === 1 ? "" : "s"} failed to send.</strong> Check the failure list below.</>
                : summary.failed === 0
                ? <><strong className="text-primary">All {summary.sent} invite{summary.sent === 1 ? "" : "s"} sent successfully.</strong></>
                : <><strong className="text-primary">{summary.sent}</strong> of {summary.total} sent · <strong className="text-destructive">{summary.failed} failed</strong></>}
            </div>
            {summary.failed > 0 && (
              <details className="mt-1">
                <summary className="mono text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer">Show failures</summary>
                <ul className="mt-1 space-y-0.5 text-[11px] mono">
                  {summary.results.filter((r) => r.status !== "sent").map((r) => (
                    <li key={r.token}><span className="text-destructive">{r.email}</span> · {r.error || "delivery failed"}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </form>
    </div>
  );
}

function InstallInviteBlock() {
  const [raw, setRaw] = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const parseEmails = () => {
    const found = raw.match(/[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g) || [];
    return Array.from(new Set(found.map((e) => e.toLowerCase()))).slice(0, 500);
  };

  const send = async () => {
    const emails = parseEmails();
    if (emails.length === 0) { toast.error("Add at least one valid email"); return; }
    setSending(true); setLastResult(null);
    try {
      const r = await api.post("/admin/install-invites", { emails });
      setLastResult(r.data);
      toast.success(`Install invites: ${r.data.sent} sent, ${r.data.failed} failed`);
      if (r.data.failed === 0) setRaw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const previewCount = parseEmails().length;

  return (
    <div className="mt-6 pt-5 border-t border-border" data-testid="install-invite-block">
      <div className="mono text-[10px] uppercase tracking-widest text-secondary flex items-center gap-1.5">
        <Download className="w-3 h-3" /> Send install invites
      </div>
      <p className="text-sm text-muted-foreground mt-1">
        Email an <span className="mono">Add DPP to your home screen</span> invite to any list — separate emails with commas, spaces, or new lines. Recipients get a branded email with a big install button pointing to <span className="mono">/install</span> on the site.
      </p>
      <Textarea
        rows={3}
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        placeholder="e.g. jane@example.com, bob@example.com …"
        maxLength={20000}
        className="mt-3"
        data-testid="install-invite-emails"
      />
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <Button
          onClick={send}
          disabled={sending || previewCount === 0}
          className="rounded-full"
          data-testid="install-invite-send"
        >
          {sending
            ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending…</>
            : <><Download className="w-3.5 h-3.5 mr-1.5" /> Send {previewCount || ""} install invite{previewCount === 1 ? "" : "s"}</>}
        </Button>
        {lastResult && (
          <span className="mono text-[10px] text-muted-foreground" data-testid="install-invite-result">
            last run: {lastResult.sent} sent · {lastResult.failed} failed
          </span>
        )}
      </div>
    </div>
  );
}



// ---------- Bulk Invite Analytics (campaign funnel: sent → open → click → redeem) ----------
function BulkInviteAnalytics({ visible, refreshSignal }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get("/admin/trial-links/analytics");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Analytics load failed");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (visible) load(); }, [visible, load, refreshSignal]);

  if (!visible) return null;

  const totals = data?.totals || null;
  const campaigns = data?.campaigns || [];

  return (
    <div className="mt-6 pt-5 border-t border-border" data-testid="bulk-analytics-block">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-secondary flex items-center gap-1.5">
            <BarChart3 className="w-3 h-3" /> Campaign analytics
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            Funnel per campaign (grouped by <span className="mono">note</span>): sent → open → click → redeem.
            Only counts links you actually emailed — copy-paste links are excluded. Invites sent without a note are grouped as <span className="mono">Uncategorized</span>.
          </div>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={load}
          disabled={loading}
          className="rounded-full"
          data-testid="analytics-refresh"
        >
          {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Refreshing…</> : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</>}
        </Button>
      </div>

      {error && <div className="text-sm text-destructive mb-3" data-testid="analytics-error">{error}</div>}

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="analytics-totals">
          <FunnelStat testId="tot-sent" icon={Mail} label="Sent" value={totals.sent} sub={`${totals.total_links} link${totals.total_links === 1 ? "" : "s"} total`} />
          <FunnelStat testId="tot-open" icon={Eye} label="Opened" value={totals.opened} sub={`${totals.open_rate}% open rate`} accent="secondary" />
          <FunnelStat testId="tot-click" icon={MousePointerClick} label="Clicked" value={totals.clicked} sub={`${totals.click_rate}% CTR`} accent="secondary" />
          <FunnelStat testId="tot-redeem" icon={CheckCircle2} label="Redeemed" value={totals.redeemed} sub={`${totals.redemption_rate}% conversion`} accent="primary" />
        </div>
      )}

      {campaigns.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-6 border border-dashed rounded-md">
          No emailed campaigns yet. Use <span className="mono">Bulk invite</span> above to send some — analytics appear here once at least one email is delivered.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border" data-testid="analytics-table-wrap">
          <table className="w-full text-sm" data-testid="analytics-table">
            <thead className="bg-muted/60">
              <tr className="text-left">
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Campaign (note)</th>
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Sent</th>
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Open</th>
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Click</th>
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground text-right">Redeem</th>
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground w-44">Funnel</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => {
                const bestRate = Math.max(c.open_rate, c.click_rate, c.redemption_rate);
                return (
                  <tr key={c.note} className="border-t border-border" data-testid={`analytics-row-${encodeURIComponent(c.note)}`}>
                    <td className="px-3 py-2 max-w-[280px] truncate" title={c.note}>{c.note}</td>
                    <td className="px-3 py-2 mono text-xs text-right">{c.sent}</td>
                    <td className="px-3 py-2 mono text-xs text-right">
                      {c.opened}
                      <span className="text-muted-foreground text-[10px] ml-1">{c.sent ? `(${c.open_rate}%)` : ""}</span>
                    </td>
                    <td className="px-3 py-2 mono text-xs text-right">
                      {c.clicked}
                      <span className="text-muted-foreground text-[10px] ml-1">{c.sent ? `(${c.click_rate}%)` : ""}</span>
                    </td>
                    <td className="px-3 py-2 mono text-xs text-right">
                      <span className={c.redeemed > 0 ? "text-primary font-semibold" : ""}>{c.redeemed}</span>
                      <span className="text-muted-foreground text-[10px] ml-1">{c.sent ? `(${c.redemption_rate}%)` : ""}</span>
                    </td>
                    <td className="px-3 py-2">
                      <FunnelBars sent={c.sent} opened={c.opened} clicked={c.clicked} redeemed={c.redeemed} />
                      {bestRate === 0 && c.sent > 0 && (
                        <div className="mono text-[9px] text-muted-foreground italic mt-1">Awaiting engagement</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="mono text-[10px] text-muted-foreground mt-2 text-right">
        Open tracking uses a 1×1 pixel — clients that block images (Apple Mail Privacy, etc.) won&apos;t count.
      </p>
    </div>
  );
}

function FunnelStat({ label, value, sub, icon: Icon, testId, accent }) {
  const accentCls =
    accent === "primary" ? "text-primary"
    : accent === "secondary" ? "text-secondary"
    : "text-foreground";
  return (
    <div className="rounded-md border border-border bg-card p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="w-3.5 h-3.5" />
        <span className="mono text-[10px] uppercase tracking-widest">{label}</span>
      </div>
      <div className={`serif text-2xl mt-1 ${accentCls}`}>{Number(value ?? 0).toLocaleString()}</div>
      {sub && <div className="mono text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

// Horizontal 3-stage funnel bars — width relative to `sent` (max=100%).
function FunnelBars({ sent, opened, clicked, redeemed }) {
  if (!sent) return <div className="text-[10px] text-muted-foreground italic">not emailed</div>;
  const pct = (n) => Math.max(2, Math.min(100, (n / sent) * 100));
  return (
    <div className="space-y-0.5">
      <FunnelBar label="O" pct={pct(opened)}  cls="bg-secondary/70" />
      <FunnelBar label="C" pct={pct(clicked)} cls="bg-secondary" />
      <FunnelBar label="R" pct={pct(redeemed)} cls="bg-primary" />
    </div>
  );
}
function FunnelBar({ label, pct, cls }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="mono text-[8px] uppercase text-muted-foreground w-3">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${cls}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}


// ---------- Contact Inbox (submissions from Pricing /api/contact form) ----------
function ContactInboxCard() {
  const [filter, setFilter] = useState("new"); // 'new' | 'handled' | 'all'
  const [data, setData] = useState({ messages: [], counts: { new: 0, handled: 0, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({}); // {id: text}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/contact-messages", { params: { status: filter } });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Inbox load failed");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setSavingId(id);
    try {
      await api.patch(`/admin/contact-messages/${id}`, { status });
      toast.success(status === "handled" ? "Marked as handled" : "Reopened");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setSavingId(null); }
  };

  const saveNote = async (id) => {
    const draft = (noteDrafts[id] || "").trim();
    setSavingId(id);
    try {
      await api.patch(`/admin/contact-messages/${id}`, { admin_note: draft });
      toast.success("Note saved");
      setNoteDrafts((prev) => { const n = { ...prev }; delete n[id]; return n; });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSavingId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this message permanently?")) return;
    setSavingId(id);
    try {
      await api.delete(`/admin/contact-messages/${id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally { setSavingId(null); }
  };

  return (
    <Card className="mt-6" data-testid="contact-inbox-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Inbox className="w-4 h-4 text-secondary" />
            <h2 className="serif text-2xl">Contact inbox</h2>
            <Badge variant="outline" className="mono text-[10px] uppercase" data-testid="inbox-new-count">
              {data.counts.new} new
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-full border border-border overflow-hidden mono text-[10px] uppercase tracking-widest" data-testid="inbox-filter">
              {["new", "handled", "all"].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 ${filter === f ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}
                  data-testid={`inbox-filter-${f}`}
                >
                  {f}{f !== "all" ? ` (${data.counts[f] || 0})` : ""}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} className="rounded-full" data-testid="inbox-refresh">
              {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Loading…</> : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</>}
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Submissions from the <span className="mono">/pricing</span> Contact-us form. Click any row to see the full message, save a triage note, mark as handled, or reply directly via mailto.
        </p>

        {data.messages.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-8 border border-dashed rounded-md" data-testid="inbox-empty">
            {filter === "new" ? "Nothing new — inbox zero" : "No messages match this filter"}
          </div>
        ) : (
          <div className="space-y-2" data-testid="inbox-list">
            {data.messages.map((m) => {
              const isOpen = expandedId === m.id;
              const isNew = m.status === "new";
              const draft = noteDrafts[m.id];
              const currentNote = draft !== undefined ? draft : (m.admin_note || "");
              return (
                <div
                  key={m.id}
                  className={`rounded-md border ${isNew ? "border-secondary/40 bg-secondary/[0.03]" : "border-border bg-card"} transition-colors`}
                  data-testid={`inbox-row-${m.id}`}
                >
                  <button
                    type="button"
                    onClick={() => setExpandedId(isOpen ? null : m.id)}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 flex-wrap"
                    data-testid={`inbox-toggle-${m.id}`}
                  >
                    <Circle className={`w-2 h-2 shrink-0 ${isNew ? "fill-secondary text-secondary" : "fill-muted text-muted"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{m.name}</span>
                        <span className="mono text-[11px] text-muted-foreground truncate">&lt;{m.email}&gt;</span>
                        {m.source === "chatbot" && (
                          <Badge variant="outline" className="mono text-[9px] uppercase tracking-widest text-secondary border-secondary/40" data-testid={`inbox-source-chatbot-${m.id}`}>
                            from chat
                          </Badge>
                        )}
                      </div>
                      <div className="mono text-[10px] text-muted-foreground mt-0.5 line-clamp-1">
                        {(m.message || "").slice(0, 140)}
                      </div>
                    </div>
                    <span className="mono text-[10px] text-muted-foreground whitespace-nowrap">{fmtDate(m.created_at)}</span>
                    {!isNew && <Badge variant="outline" className="mono text-[10px] uppercase">handled</Badge>}
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 border-t border-border/60 space-y-3" data-testid={`inbox-body-${m.id}`}>
                      <div className="text-sm whitespace-pre-wrap bg-muted/40 rounded-md p-3 border border-border/60" data-testid={`inbox-message-${m.id}`}>
                        {m.message}
                      </div>
                      {m.chat_transcript && m.chat_transcript.length > 0 && (
                        <details className="rounded-md border border-secondary/30 bg-secondary/[0.04]" data-testid={`inbox-transcript-${m.id}`}>
                          <summary className="cursor-pointer px-3 py-2 mono text-[10px] uppercase tracking-widest text-secondary hover:bg-secondary/10 transition-colors">
                            Chatbot transcript ({m.chat_transcript.length} messages)
                          </summary>
                          <div className="p-3 pt-1 space-y-1.5">
                            {m.chat_transcript.map((t, i) => (
                              <div key={i} className={`text-xs leading-relaxed ${t.role === "user" ? "text-foreground" : "text-muted-foreground"}`}>
                                <span className="mono text-[9px] uppercase tracking-widest mr-2">{t.role === "user" ? "USER" : "BOT"}</span>
                                {t.content}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                      <div className="mono text-[11px] text-muted-foreground grid grid-cols-2 gap-x-4 gap-y-1">
                        <div>Sent: {fmtDate(m.created_at)}</div>
                        <div>Delivery: {m.delivered ? "✓ Emailed to info@" : "✗ email failed"}</div>
                        {m.handled_at && <div>Handled: {fmtDate(m.handled_at)}</div>}
                        {m.handled_by && <div>By: {m.handled_by}</div>}
                      </div>

                      <div>
                        <Label htmlFor={`note-${m.id}`} className="text-xs">Internal note</Label>
                        <Textarea
                          id={`note-${m.id}`}
                          rows={2}
                          value={currentNote}
                          onChange={(e) => setNoteDrafts((prev) => ({ ...prev, [m.id]: e.target.value }))}
                          placeholder="e.g. Replied 8/28 with pricing PDF"
                          data-testid={`inbox-note-${m.id}`}
                        />
                        {draft !== undefined && draft !== (m.admin_note || "") && (
                          <div className="mt-2 flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => saveNote(m.id)} disabled={savingId === m.id} data-testid={`inbox-note-save-${m.id}`}>
                              {savingId === m.id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <MessageSquare className="w-3.5 h-3.5 mr-1.5" />} Save note
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setNoteDrafts((p) => { const n = { ...p }; delete n[m.id]; return n; })}>Cancel</Button>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 pt-1">
                        <a
                          href={`mailto:${m.email}?subject=${encodeURIComponent("Re: your message to DPP")}&body=${encodeURIComponent("Hi " + (m.name.split(" ")[0] || "there") + ",\n\nThanks for reaching out — ")}`}
                          data-testid={`inbox-reply-${m.id}`}
                        >
                          <Button size="sm" variant="outline" className="rounded-full">
                            <Reply className="w-3.5 h-3.5 mr-1.5" /> Reply via email
                          </Button>
                        </a>
                        {isNew ? (
                          <Button
                            size="sm" variant="outline" className="rounded-full"
                            onClick={() => setStatus(m.id, "handled")}
                            disabled={savingId === m.id}
                            data-testid={`inbox-handle-${m.id}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark handled
                          </Button>
                        ) : (
                          <Button
                            size="sm" variant="outline" className="rounded-full"
                            onClick={() => setStatus(m.id, "new")}
                            disabled={savingId === m.id}
                            data-testid={`inbox-reopen-${m.id}`}
                          >
                            <Circle className="w-3.5 h-3.5 mr-1.5" /> Reopen
                          </Button>
                        )}
                        <Button
                          size="sm" variant="ghost" className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10 ml-auto"
                          onClick={() => remove(m.id)}
                          disabled={savingId === m.id}
                          data-testid={`inbox-delete-${m.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ---------- Admin Accounts (create db-backed admins with password) ----------
function AdminAccountsCard() {
  const [data, setData] = useState({ admins: [], counts: { env: 0, db: 0 } });
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [note, setNote] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [busyEmail, setBusyEmail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/admin-users");
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const genPw = () => {
    // Client-side random password suggestion. bcrypt happens server-side.
    const alpha = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz";
    const nums = "23456789";
    const syms = "!@#$%*_-";
    const all = alpha + nums + syms;
    const arr = new Uint32Array(16);
    (window.crypto || window.msCrypto).getRandomValues(arr);
    let out = "";
    for (let i = 0; i < 16; i++) out += all[arr[i] % all.length];
    // Force at least one of each class for policy-compliant defaults.
    out = alpha[arr[0] % alpha.length] + nums[arr[1] % nums.length] + syms[arr[2] % syms.length] + out.slice(3);
    setPassword(out);
    setShowPw(true);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (!email.trim() || !email.includes("@")) { toast.error("Valid email required"); return; }
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/admin/admin-users", {
        name: name.trim(), email: email.trim(), password, note: note.trim() || undefined,
      });
      toast.success(r.data.user_created ? "Admin created" : "Existing user promoted to admin");
      setName(""); setEmail(""); setPassword(""); setNote(""); setShowPw(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setSubmitting(false); }
  };

  const revoke = async (targetEmail) => {
    if (!window.confirm(`Revoke admin access for ${targetEmail}?\nTheir user account stays — only the admin grant is removed.`)) return;
    setBusyEmail(targetEmail);
    try {
      await api.delete(`/admin/admin-users/${encodeURIComponent(targetEmail)}`);
      toast.success("Admin revoked");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    } finally { setBusyEmail(null); }
  };

  const copyPw = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      toast.success("Password copied");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <Card className="mt-6" data-testid="admin-accounts-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <ShieldPlus className="w-4 h-4 text-secondary" />
            <h2 className="serif text-2xl">Admin accounts</h2>
            <Badge variant="outline" className="mono text-[10px] uppercase" data-testid="admin-accounts-count">
              {(data.counts.env || 0) + (data.counts.db || 0)} total
            </Badge>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="rounded-full" data-testid="admin-accounts-refresh">
            {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Loading…</> : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</>}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Create a new admin by giving them a <span className="mono">name</span>, <span className="mono">email</span>, and <span className="mono">password</span>. If the email is already a DPP user they get promoted (password stays intact). Otherwise a new password-auth account is created and the admin grant is added. Env-defined admins (via <span className="mono">ADMIN_EMAILS</span>) are immutable here — edit the env var and redeploy to change them.
        </p>

        {/* Create form */}
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="admin-accounts-form">
          <div>
            <Label htmlFor="ac-name">Name</Label>
            <Input id="ac-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Ops" required data-testid="admin-accounts-name" />
          </div>
          <div>
            <Label htmlFor="ac-email">Email</Label>
            <Input id="ac-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@dp.com" required data-testid="admin-accounts-email" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="ac-password">Password</Label>
            <div className="flex gap-2">
              <Input
                id="ac-password"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 8 characters"
                minLength={8}
                required
                autoComplete="new-password"
                className="flex-1 mono text-sm"
                data-testid="admin-accounts-password"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => setShowPw((v) => !v)} title={showPw ? "Hide" : "Show"} data-testid="admin-accounts-show-pw">
                {showPw ? "Hide" : "Show"}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={genPw} title="Generate strong password" data-testid="admin-accounts-generate-pw">
                <KeyRound className="w-3.5 h-3.5" />
              </Button>
              {password && (
                <Button type="button" variant="outline" size="sm" onClick={copyPw} title="Copy" data-testid="admin-accounts-copy-pw">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
            <div className="mono text-[10px] text-muted-foreground mt-1">
              Share this password with the new admin through a secure channel — DPP does not email it.
            </div>
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="ac-note">Note (optional)</Label>
            <Input id="ac-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Contract Ops, temp support until 3/31" maxLength={300} data-testid="admin-accounts-note" />
          </div>
          <div className="md:col-span-2">
            <Button type="submit" disabled={submitting} className="rounded-full" data-testid="admin-accounts-submit">
              {submitting
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</>
                : <><ShieldPlus className="w-4 h-4 mr-2" /> Create admin</>}
            </Button>
          </div>
        </form>

        {/* Current admins list */}
        <div className="rounded-md border border-border overflow-hidden" data-testid="admin-accounts-list">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr className="text-left">
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Email</th>
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Name</th>
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Source</th>
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground">Granted</th>
                <th className="px-3 py-2 mono text-[10px] uppercase tracking-widest text-muted-foreground w-24 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {data.admins.length === 0 ? (
                <tr><td colSpan="5" className="px-3 py-6 text-center text-muted-foreground text-sm">No admins</td></tr>
              ) : data.admins.map((a) => (
                <tr key={a.email + a.source} className="border-t border-border" data-testid={`admin-row-${a.email}`}>
                  <td className="px-3 py-2 mono text-xs">{a.email}</td>
                  <td className="px-3 py-2">{a.name || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className="mono text-[10px] uppercase" title={a.source === "env" ? "Configured via ADMIN_EMAILS env var" : "Created from this admin panel"}>
                      {a.source === "env" ? <><Lock className="w-3 h-3 mr-1" />env</> : "db"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 mono text-[11px] text-muted-foreground">
                    {a.granted_at ? fmtDate(a.granted_at) : <span>—</span>}
                    {a.granted_by && <div className="text-[10px]">by {a.granted_by}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {a.revocable ? (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => revoke(a.email)}
                        disabled={busyEmail === a.email}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`admin-revoke-${a.email}`}
                      >
                        {busyEmail === a.email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    ) : (
                      <span className="mono text-[10px] text-muted-foreground italic">immutable</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}


// ---------- Admin Activity Log (audit trail) ----------
// Sub-colored icon mapping so scanning the log is visually parseable at a glance.
const ACTION_STYLE = {
  create_admin:      { color: "text-primary",     bg: "bg-primary/10",     Icon: ShieldPlus },
  revoke_admin:      { color: "text-destructive", bg: "bg-destructive/10", Icon: ShieldOff },
  grant_pro:         { color: "text-primary",     bg: "bg-primary/10",     Icon: Crown },
  revoke_pro:        { color: "text-destructive", bg: "bg-destructive/10", Icon: Ban },
  delete_user:       { color: "text-destructive", bg: "bg-destructive/10", Icon: Trash2 },
  bulk_delete_users: { color: "text-destructive", bg: "bg-destructive/10", Icon: Trash2 },
  preapprove_users:  { color: "text-secondary",   bg: "bg-secondary/10",   Icon: UserPlus },
  create_trial_link: { color: "text-secondary",   bg: "bg-secondary/10",   Icon: Link2 },
  email_trial_link:  { color: "text-secondary",   bg: "bg-secondary/10",   Icon: Mail },
  bulk_trial_invite: { color: "text-secondary",   bg: "bg-secondary/10",   Icon: Mail },
  revoke_trial_link: { color: "text-muted-foreground", bg: "bg-muted",     Icon: Ban },
  delete_trial_link: { color: "text-destructive", bg: "bg-destructive/10", Icon: Trash2 },
  mark_contact_handled: { color: "text-primary", bg: "bg-primary/10",     Icon: CheckCircle2 },
  reopen_contact:    { color: "text-secondary",   bg: "bg-secondary/10",   Icon: RefreshCw },
  delete_contact:    { color: "text-destructive", bg: "bg-destructive/10", Icon: Trash2 },
  note_contact:      { color: "text-muted-foreground", bg: "bg-muted",     Icon: MessageSquare },
};

function ActivityLogCard() {
  const [data, setData] = useState({ activity: [], counts_30d: {}, action_labels: {} });
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");
  const [actorFilter, setActorFilter] = useState("");
  const [targetFilter, setTargetFilter] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (actionFilter) params.action = actionFilter;
      if (actorFilter.trim()) params.actor = actorFilter.trim();
      if (targetFilter.trim()) params.target = targetFilter.trim();
      const r = await api.get("/admin/activity", { params });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Activity load failed");
    } finally { setLoading(false); }
  }, [actionFilter, actorFilter, targetFilter]);

  useEffect(() => { load(); }, [load]);

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(data.activity, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dpp-admin-activity-${new Date().toISOString().slice(0,10)}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("Downloaded");
  };

  const totalCount = Object.values(data.counts_30d).reduce((a, b) => a + b, 0);
  const sortedCounts = Object.entries(data.counts_30d).sort((a, b) => b[1] - a[1]);

  return (
    <Card className="mt-6" data-testid="activity-log-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-secondary" />
            <h2 className="serif text-2xl">Admin activity log</h2>
            <Badge variant="outline" className="mono text-[10px] uppercase" data-testid="activity-count-30d">
              {totalCount} in 30d
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportJson} disabled={!data.activity.length} className="rounded-full" data-testid="activity-export">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export JSON
            </Button>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} className="rounded-full" data-testid="activity-refresh">
              {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Loading…</> : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</>}
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Every admin mutation (create/revoke admin, grant/revoke Pro, delete user, trial-link ops, contact triage) is recorded with actor, timestamp, target, and details. Filters below are additive.
        </p>

        {/* 30d activity summary chips */}
        {sortedCounts.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4" data-testid="activity-summary">
            {sortedCounts.slice(0, 8).map(([action, count]) => (
              <button
                key={action}
                type="button"
                onClick={() => setActionFilter(actionFilter === action ? "" : action)}
                className={`mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${actionFilter === action ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/40 hover:text-primary"}`}
                data-testid={`activity-chip-${action}`}
              >
                {(data.action_labels[action] || action)} · {count}
              </button>
            ))}
          </div>
        )}

        {/* Filter bar */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="text-sm px-3 py-2 rounded-md border border-border bg-background"
            data-testid="activity-filter-action"
          >
            <option value="">All actions</option>
            {Object.entries(data.action_labels).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <Input
            placeholder="Actor email"
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
            data-testid="activity-filter-actor"
          />
          <Input
            placeholder="Target email"
            value={targetFilter}
            onChange={(e) => setTargetFilter(e.target.value)}
            data-testid="activity-filter-target"
          />
          {(actionFilter || actorFilter || targetFilter) && (
            <Button
              variant="ghost" size="sm"
              onClick={() => { setActionFilter(""); setActorFilter(""); setTargetFilter(""); }}
              className="rounded-full"
              data-testid="activity-filter-clear"
            >
              Clear
            </Button>
          )}
        </div>

        {data.activity.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-8 border border-dashed rounded-md" data-testid="activity-empty">
            No activity matches the current filters.
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="activity-list">
            {data.activity.map((r) => {
              const style = ACTION_STYLE[r.action] || { color: "text-muted-foreground", bg: "bg-muted", Icon: ScrollText };
              const Icon = style.Icon;
              const isOpen = expanded === r.id;
              const hasDetails = r.details && Object.keys(r.details).length > 0;
              return (
                <div
                  key={r.id}
                  className="rounded-md border border-border bg-card"
                  data-testid={`activity-row-${r.id}`}
                >
                  <button
                    type="button"
                    onClick={() => hasDetails && setExpanded(isOpen ? null : r.id)}
                    className={`w-full text-left px-3 py-2 flex items-start gap-3 ${hasDetails ? "cursor-pointer hover:bg-muted/30" : "cursor-default"}`}
                    data-testid={`activity-toggle-${r.id}`}
                  >
                    <div className={`w-8 h-8 rounded-md ${style.bg} ${style.color} grid place-items-center shrink-0`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-medium ${style.color}`}>{r.label || r.action}</span>
                        {r.target_email && (
                          <span className="mono text-[11px] text-muted-foreground truncate">→ {r.target_email}</span>
                        )}
                      </div>
                      <div className="mono text-[10px] text-muted-foreground mt-0.5">
                        by {r.actor_email || "—"}
                        {r.ip && <span className="ml-2">· ip {r.ip}</span>}
                      </div>
                    </div>
                    <div className="mono text-[10px] text-muted-foreground whitespace-nowrap shrink-0">
                      {fmtDate(r.at)}
                    </div>
                  </button>

                  {isOpen && hasDetails && (
                    <div className="border-t border-border/60 px-3 py-2" data-testid={`activity-details-${r.id}`}>
                      <pre className="mono text-[11px] text-muted-foreground whitespace-pre-wrap break-all">
{JSON.stringify(r.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ---------- Traffic / visitor analytics ----------
function TrafficCard() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/analytics/traffic", { params: { days } });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Traffic load failed");
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const daily = data?.daily || [];
  const maxViews = Math.max(1, ...daily.map((d) => d.views));

  return (
    <Card className="mt-6" data-testid="traffic-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-secondary" />
            <h2 className="serif text-2xl">Site traffic</h2>
            <Badge variant="outline" className="mono text-[10px] uppercase" data-testid="traffic-window">
              last {days} days
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-full border border-border overflow-hidden mono text-[10px] uppercase tracking-widest" data-testid="traffic-days">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  className={`px-3 py-1.5 ${days === d ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}
                  data-testid={`traffic-days-${d}`}
                >
                  {d}d
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} className="rounded-full" data-testid="traffic-refresh">
              {loading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Loading…</> : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</>}
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          First-party page-view analytics. Bot traffic (crawlers, headless browsers) is filtered out. Unique visitors are approximated by a per-day salted hash of IP + user-agent — no cookies, no third-party trackers.
        </p>

        {data && (
          <>
            {/* Totals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="traffic-totals">
              <TrafficStat testId="traf-views" icon={Eye} label="Total views" value={data.totals.views} sub={`${data.totals.views_7d.toLocaleString()} in last 7d`} />
              <TrafficStat testId="traf-uniques" icon={Users} label="Unique visitors" value={data.totals.unique_visitors} sub={`${data.totals.unique_visitors_7d.toLocaleString()} in last 7d`} accent="primary" />
              <TrafficStat testId="traf-per-visitor" icon={Activity} label="Views / visitor" value={data.totals.unique_visitors > 0 ? (data.totals.views / data.totals.unique_visitors).toFixed(2) : "0"} sub="Session depth" />
              <TrafficStat testId="traf-bots" icon={Globe} label="Bot hits" value={data.totals.bot_views} sub="Excluded from totals" accent="muted" />
            </div>

            {/* Daily sparkline (bar chart) */}
            {daily.length > 0 && (
              <div className="mb-6" data-testid="traffic-sparkline">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Daily views</div>
                <div className="flex items-end gap-1 h-24 border-b border-border/60 pb-1">
                  {daily.map((d) => {
                    const h = Math.max(4, (d.views / maxViews) * 88);
                    return (
                      <div
                        key={d.day}
                        className="flex-1 min-w-[4px] bg-primary/70 hover:bg-primary transition-colors rounded-t-sm relative group"
                        style={{ height: `${h}px` }}
                        title={`${d.day}: ${d.views} views · ${d.unique_visitors} uniques`}
                        data-testid={`traffic-bar-${d.day}`}
                      >
                        <div className="hidden group-hover:block absolute -top-8 left-1/2 -translate-x-1/2 bg-background border border-border rounded-md px-2 py-1 mono text-[10px] whitespace-nowrap shadow-sm z-10">
                          {d.day} · {d.views}v / {d.unique_visitors}u
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mono text-[9px] text-muted-foreground mt-1">
                  <span>{daily[0]?.day}</span>
                  <span>{daily[daily.length - 1]?.day}</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Top pages */}
              <div data-testid="traffic-top-pages">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Top pages</div>
                {data.top_pages.length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">No page data yet</div>
                ) : (
                  <div className="space-y-1">
                    {data.top_pages.map((p) => (
                      <TrafficRow key={p.path} label={p.path || "(root)"} views={p.views} uniques={p.unique_visitors} maxViews={data.top_pages[0].views} testId={`traffic-page-${p.path.replace(/\W/g, "-")}`} />
                    ))}
                  </div>
                )}
              </div>

              {/* Top referrers */}
              <div data-testid="traffic-top-referrers">
                <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Top referrers</div>
                {data.top_referrers.length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">Direct traffic only — no external referrers yet</div>
                ) : (
                  <div className="space-y-1">
                    {data.top_referrers.map((r) => (
                      <TrafficRow key={r.referrer} label={hostOf(r.referrer)} sub={r.referrer} views={r.views} maxViews={data.top_referrers[0].views} testId={`traffic-ref-${hostOf(r.referrer).replace(/\W/g, "-")}`} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function NewsletterCard() {
  const [stats, setStats] = useState({ active: 0, total: 0, subscribers: [] });
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewEmail, setPreviewEmail] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/newsletter/subscribers");
      setStats(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Newsletter load failed");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runPreview = async (emailOverride) => {
    setPreviewLoading(true);
    try {
      const params = {};
      const em = (emailOverride ?? previewEmail).trim();
      if (em) params.email = em;
      const r = await api.get("/admin/newsletter/preview", { params, responseType: "text", transformResponse: (v) => v });
      setPreviewHtml(r.data);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally { setPreviewLoading(false); }
  };

  const sendNow = async () => {
    if (!window.confirm(`Send the weekly digest to ${stats.active} subscriber${stats.active === 1 ? "" : "s"} right now?`)) return;
    setSending(true);
    try {
      const r = await api.post("/admin/newsletter/send", {});
      toast.success(`Dispatched — sent ${r.data.sent}, failed ${r.data.failed}, personalized ${r.data.personalized}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const recent = (stats.subscribers || []).slice(0, 5);

  return (
    <Card className="mt-6" data-testid="newsletter-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Newspaper className="w-4 h-4 text-secondary" />
            <h2 className="serif text-2xl">Weekly newsletter</h2>
            <Badge variant="outline" className="mono text-[10px] uppercase" data-testid="newsletter-active-count">
              {stats.active} active
            </Badge>
            {stats.total > stats.active && (
              <Badge variant="outline" className="mono text-[10px] uppercase text-muted-foreground">
                {stats.total - stats.active} unsub
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              type="email"
              value={previewEmail}
              onChange={(e) => setPreviewEmail(e.target.value)}
              placeholder="preview as… (defaults to you)"
              className="h-8 w-56 rounded-full text-sm"
              data-testid="newsletter-preview-email"
            />
            <Button
              variant="outline" size="sm"
              onClick={() => runPreview()}
              disabled={previewLoading}
              className="rounded-full"
              data-testid="newsletter-preview-btn"
            >
              {previewLoading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Rendering…</> : <><Eye className="w-3.5 h-3.5 mr-1.5" /> Preview digest</>}
            </Button>
            <Button
              variant="default" size="sm"
              onClick={sendNow}
              disabled={sending || stats.active === 0}
              className="rounded-full"
              data-testid="newsletter-send-btn"
            >
              {sending ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending…</> : <><Send className="w-3.5 h-3.5 mr-1.5" /> Send now</>}
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Digest auto-sends every Saturday at 08:00 America/New_York. Preview lets you proofread this week&apos;s HTML — with an email address it shows the personalized version (top-of-mail holdings block + ex-div this week).
        </p>

        {loading ? (
          <div className="h-16 rounded-md bg-muted animate-pulse" />
        ) : recent.length === 0 ? (
          <div className="text-sm text-muted-foreground italic" data-testid="newsletter-empty">
            No subscribers yet. Once someone taps the newsletter icon in the nav, they show up here.
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="newsletter-recent-list">
            {recent.map((s) => (
              <div key={s.email} className="grid grid-cols-[1fr_auto] items-center gap-3 text-sm border-b border-border/40 py-1.5 last:border-b-0">
                <div className="min-w-0">
                  <div className="truncate">{s.email}{s.name ? <span className="text-muted-foreground"> · {s.name}</span> : null}</div>
                  <div className="mono text-[10px] text-muted-foreground">
                    Since {fmtDate(s.subscribed_at)}{s.last_sent_at ? ` · last sent ${fmtDate(s.last_sent_at)}` : " · not yet sent"}
                  </div>
                </div>
                <Button
                  variant="ghost" size="sm"
                  onClick={() => { setPreviewEmail(s.email); runPreview(s.email); }}
                  className="rounded-full text-xs"
                  data-testid={`newsletter-preview-as-${s.email}`}
                >
                  Preview as
                </Button>
              </div>
            ))}
            {stats.subscribers.length > recent.length && (
              <div className="mono text-[10px] text-muted-foreground pt-1">+ {stats.subscribers.length - recent.length} more subscriber{stats.subscribers.length - recent.length === 1 ? "" : "s"}</div>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="newsletter-preview-dialog">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="serif text-xl">Weekly digest preview</DialogTitle>
            <DialogDescription>
              Rendered exactly as subscribers will see it. Unsubscribe token is a placeholder — do not click.
            </DialogDescription>
          </DialogHeader>
          <div className="border-t border-border bg-muted/30">
            <iframe
              title="Newsletter preview"
              srcDoc={previewHtml}
              className="w-full h-[70vh] bg-white"
              sandbox=""
              data-testid="newsletter-preview-iframe"
            />
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


function TrafficStat({ label, value, sub, icon: Icon, testId, accent }) {
  const accentCls = accent === "primary" ? "text-primary" : accent === "muted" ? "text-muted-foreground" : "text-foreground";
  return (
    <div className="rounded-md border border-border bg-card p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="w-3.5 h-3.5" />
        <span className="mono text-[10px] uppercase tracking-widest">{label}</span>
      </div>
      <div className={`serif text-2xl mt-1 ${accentCls}`}>{typeof value === "number" ? value.toLocaleString() : value}</div>
      {sub && <div className="mono text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function TrafficRow({ label, sub, views, uniques, maxViews, testId }) {
  const pct = Math.max(4, Math.min(100, (views / (maxViews || 1)) * 100));
  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-2" data-testid={testId}>
      <div className="min-w-0">
        <div className="text-sm truncate" title={sub || label}>{label}</div>
        <div className="h-1 rounded-full bg-muted overflow-hidden mt-0.5">
          <div className="h-full bg-secondary/70" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="mono text-[11px] text-muted-foreground whitespace-nowrap">
        {views.toLocaleString()}
        {uniques !== undefined && <span className="ml-1 text-[10px]">/ {uniques}u</span>}
      </div>
    </div>
  );
}

function hostOf(url) {
  if (!url) return "—";
  try { return new URL(url).hostname; } catch { return url.split("/")[0]; }
}


// ---------- Trust Center admin ----------
function TrustCenterAdminCard() {
  const [docs, setDocs] = useState([]);
  const [storageOk, setStorageOk] = useState(true);
  const [loading, setLoading] = useState(true);
  const [uploadingSlug, setUploadingSlug] = useState(null);
  const [notes, setNotes] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newBody, setNewBody] = useState("");
  const [newRequired, setNewRequired] = useState(false);
  const [creating, setCreating] = useState(false);
  // Edit-existing dialog state
  const [editDoc, setEditDoc] = useState(null);   // the doc being edited (or null)
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editBody, setEditBody] = useState("");
  const [editRequired, setEditRequired] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (d) => {
    setEditDoc(d);
    setEditTitle(d.title || "");
    setEditDesc(d.description || "");
    setEditBody(d.body_md || "");
    setEditRequired(!!d.required_for_signup);
  };
  const submitEdit = async () => {
    if (!editDoc) return;
    setEditSaving(true);
    try {
      await api.patch(`/admin/policy-documents/${editDoc.slug}`, {
        title: editTitle.trim(),
        description: editDesc.trim() || null,
        body_md: editBody.trim() || null,
        required_for_signup: editRequired,
      });
      toast.success(`Saved "${editTitle}"`);
      setEditDoc(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setEditSaving(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/policy-documents");
      setDocs(r.data.documents || []);
      setStorageOk(!!r.data.storage_configured);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load policies");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const uploadFile = async (slug, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("PDF files only");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File exceeds 25 MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("notes", notes[slug] || "");
    setUploadingSlug(slug);
    try {
      const r = await api.post(`/admin/policy-documents/${slug}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${slug} uploaded as v${r.data.version} (${Math.round(r.data.size_bytes / 1024)} KB)`);
      setNotes((n) => ({ ...n, [slug]: "" }));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploadingSlug(null); }
  };

  const toggleActive = async (slug, next) => {
    try {
      await api.patch(`/admin/policy-documents/${slug}`, { active: next });
      toast.success(`${slug} ${next ? "activated" : "deactivated"}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <Card className="mt-6" data-testid="trust-admin-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-secondary" />
            <h2 className="serif text-2xl">Trust Center Library</h2>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="mono text-[10px] uppercase" data-testid="trust-doc-count">{docs.length} policies</Badge>
            <Button size="sm" onClick={() => { setNewSlug(""); setNewTitle(""); setNewDesc(""); setNewRequired(false); setCreateOpen(true); }} className="rounded-full" data-testid="trust-new-policy-btn">
              <PlusCircleIcon className="w-4 h-4 mr-1.5" /> New policy
            </Button>
          </div>
        </div>
        {!storageOk && (
          <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            Object storage not configured — set <code className="mono">EMERGENT_LLM_KEY</code> in backend/.env to enable uploads.
          </div>
        )}
        <p className="text-sm text-muted-foreground mb-4">
          Upload the latest PDF for each policy. Every new upload increments the version — users who had already accepted the previous version will be prompted to re-accept on their next visit. Grandfathered users (pre-existing accounts) are auto-accepted at whatever version is current the moment they check in for the first time.
        </p>
        {loading ? (
          <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-md bg-muted animate-pulse" />)}</div>
        ) : (
          <div className="space-y-3" data-testid="trust-admin-list">
            {docs.map((d) => (
              <div key={d.slug} className="rounded-md border border-border p-4" data-testid={`trust-admin-row-${d.slug}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <FileText className="w-4 h-4 text-primary" />
                      <span className="serif text-lg">{d.title}</span>
                      {d.required_for_signup && (
                        <Badge variant="outline" className="mono text-[9px] uppercase tracking-widest text-secondary border-secondary/40">
                          Required at signup
                        </Badge>
                      )}
                      {!d.active && (
                        <Badge variant="outline" className="mono text-[9px] uppercase tracking-widest text-muted-foreground">Inactive</Badge>
                      )}
                    </div>
                    <div className="mono text-[10px] text-muted-foreground mt-1">
                      slug: <span className="text-foreground">{d.slug}</span>
                      {" · "}version: <span className="text-foreground">v{d.current_version || 0}</span>
                      {d.current_updated_at && <> {" · "} updated {fmtDate(d.current_updated_at)}</>}
                      {" · "} acceptances at current: <span className="text-foreground">{d.current_acceptance_count || 0}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{d.description}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {d.current_storage_path ? (
                      <a
                        href={`${API}/trust/documents/${d.slug}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        data-testid={`trust-admin-download-${d.slug}`}
                      >
                        <DownloadIcon className="w-3 h-3" /> current PDF
                      </a>
                    ) : (
                      <span className="text-[11px] italic text-muted-foreground">no file yet</span>
                    )}
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => openEdit(d)}
                      className="rounded-full text-xs"
                      data-testid={`trust-admin-edit-${d.slug}`}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => toggleActive(d.slug, !d.active)}
                      className="rounded-full text-xs"
                      data-testid={`trust-admin-toggle-${d.slug}`}
                    >
                      {d.active ? "Deactivate" : "Activate"}
                    </Button>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <Input
                    placeholder="version notes (optional, e.g. 'add DPA'), stored in audit log"
                    value={notes[d.slug] || ""}
                    onChange={(e) => setNotes((n) => ({ ...n, [d.slug]: e.target.value }))}
                    className="h-8 text-sm rounded-full flex-1 min-w-[220px]"
                    data-testid={`trust-admin-notes-${d.slug}`}
                  />
                  <label className="inline-flex" htmlFor={`file-${d.slug}`}>
                    <input
                      id={`file-${d.slug}`}
                      type="file"
                      accept="application/pdf,.pdf"
                      className="hidden"
                      onChange={(e) => uploadFile(d.slug, e.target.files?.[0])}
                      data-testid={`trust-admin-file-${d.slug}`}
                    />
                    <Button asChild variant="default" size="sm" disabled={uploadingSlug === d.slug || !storageOk} className="rounded-full">
                      <span>
                        {uploadingSlug === d.slug ? (
                          <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Uploading…</>
                        ) : (
                          <><Upload className="w-3.5 h-3.5 mr-1.5" /> Upload new version</>
                        )}
                      </span>
                    </Button>
                  </label>
                </div>
                {d.versions && d.versions.length > 0 && (
                  <details className="mt-3">
                    <summary className="mono text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer hover:text-foreground">
                      Version history ({d.versions.length})
                    </summary>
                    <div className="mt-2 space-y-1 text-xs">
                      {d.versions.map((v) => (
                        <div key={v.version} className="flex items-center justify-between gap-2 py-1 border-b border-border/40 last:border-b-0">
                          <span className="mono">v{v.version} · {fmtDate(v.uploaded_at)} · {Math.round(v.size_bytes / 1024)} KB</span>
                          <span className="text-muted-foreground truncate">{v.notes || v.original_filename}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Create new policy dialog — for adding Affiliate T&Cs, DPA, etc. */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="trust-create-policy-dialog">
          <DialogHeader>
            <DialogTitle className="serif text-2xl">Add a new policy</DialogTitle>
            <DialogDescription>
              Creates the policy shell first. You'll then upload the actual PDF from the row that appears in the list.
              Great for Affiliate / Referral T&Cs, DPA, or anything else beyond the seeded Terms · Privacy · Cookie policies.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>URL slug</Label>
              <Input
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                placeholder="affiliate-terms"
                data-testid="trust-new-slug"
              />
              <div className="mono text-[10px] text-muted-foreground mt-1">
                3–40 chars, lowercase letters/digits/hyphen. Immutable after creation.
              </div>
            </div>
            <div>
              <Label>Title</Label>
              <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Affiliate / Referral Terms & Conditions" data-testid="trust-new-title" />
            </div>
            <div>
              <Label>Description (public)</Label>
              <Textarea rows={2} value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Terms that apply to every DPP affiliate, influencer, and referrer." data-testid="trust-new-desc" />
            </div>
            <div>
              <Label>Inline body (optional · Markdown)</Label>
              <Textarea
                rows={8}
                value={newBody}
                onChange={(e) => setNewBody(e.target.value)}
                placeholder="Paste the policy text here. Renders inline on the Trust Center as a read-in-place doc — no PDF upload required.&#10;&#10;Supports basic Markdown (## headings, **bold**, - bullets, links)."
                data-testid="trust-new-body"
                className="font-mono text-xs"
              />
              <div className="mono text-[10px] text-muted-foreground mt-1">
                Leave blank if you plan to upload a PDF instead. Either surface (PDF or inline body) makes the policy live on /trust.
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={newRequired} onChange={(e) => setNewRequired(e.target.checked)} className="mt-0.5" data-testid="trust-new-required" />
              <span>
                <b>Required for signup?</b>
                <div className="text-xs text-muted-foreground">If checked, every new account must accept the current version — like Terms and Privacy do today. Uncheck for optional/informational docs.</div>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!newSlug.trim() || !newTitle.trim()) { toast.error("Slug and title required"); return; }
                setCreating(true);
                try {
                  await api.post("/admin/policy-documents", {
                    slug: newSlug.trim(),
                    title: newTitle.trim(),
                    description: newDesc.trim() || undefined,
                    body_md: newBody.trim() || undefined,
                    required: newRequired,
                  });
                  toast.success(newBody.trim() ? `"${newTitle}" is live on /trust` : `"${newTitle}" created — now upload its PDF`);
                  setCreateOpen(false);
                  setNewSlug(""); setNewTitle(""); setNewDesc(""); setNewBody(""); setNewRequired(false);
                  load();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Create failed");
                } finally { setCreating(false); }
              }}
              disabled={creating || !newSlug.trim() || !newTitle.trim()}
              className="rounded-full"
              data-testid="trust-new-submit"
            >
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit existing policy dialog — supports body_md rewrite for any doc
          (both seeded and admin-created). Once saved, the row is flagged
          `edited_by_admin: true` so startup seeds stop overwriting it. */}
      <Dialog open={!!editDoc} onOpenChange={(o) => !o && setEditDoc(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="trust-edit-dialog">
          <DialogHeader>
            <DialogTitle className="serif text-2xl">Edit {editDoc?.title}</DialogTitle>
            <DialogDescription>
              slug: <span className="mono text-xs">{editDoc?.slug}</span>
              {editDoc?.edited_by_admin && (
                <span className="ml-2 mono text-[10px] uppercase tracking-widest text-secondary">· admin-edited (seed no longer syncs)</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} data-testid="trust-edit-title" />
            </div>
            <div>
              <Label>Public description</Label>
              <Textarea rows={2} value={editDesc} onChange={(e) => setEditDesc(e.target.value)} data-testid="trust-edit-desc" />
            </div>
            <div>
              <Label>Inline body (Markdown)</Label>
              <Textarea
                rows={16}
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                className="font-mono text-xs"
                placeholder="Empty out to hide the inline body (leaves the PDF, if any, as the only surface)."
                data-testid="trust-edit-body"
              />
              <div className="mono text-[10px] text-muted-foreground mt-1">
                {editBody.length.toLocaleString()} / 30,000 characters · Supports ## H2, ### H3, - bullets, 1. numbered, **bold**, *italic*, [links](url), `code`.
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={editRequired}
                onChange={(e) => setEditRequired(e.target.checked)}
                className="mt-0.5"
                data-testid="trust-edit-required"
              />
              <span>
                <b>Required at signup</b>
                <div className="text-xs text-muted-foreground">If checked, new users must accept this policy version during signup.</div>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDoc(null)}>Cancel</Button>
            <Button onClick={submitEdit} disabled={editSaving || !editTitle.trim()} className="rounded-full" data-testid="trust-edit-submit">
              {editSaving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


// ---------- Chatbot admin: analytics + KB editor ----------
function ChatbotAdminCard() {
  const [analytics, setAnalytics] = useState(null);
  const [kb, setKb] = useState("");
  const [kbMeta, setKbMeta] = useState({ is_default: true, updated_at: null, updated_by: null });

  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openSessionId, setOpenSessionId] = useState(null);
  const [promoteState, setPromoteState] = useState(null); // { question, answer, category }
  const [promoting, setPromoting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, kbRes] = await Promise.all([
        api.get("/admin/chatbot/analytics"),
        api.get("/admin/chatbot/kb"),
      ]);
      setAnalytics(aRes.data);
      setKb(kbRes.data.kb);
      setKbMeta({
        is_default: kbRes.data.is_default,
        updated_at: kbRes.data.updated_at,
        updated_by: kbRes.data.updated_by,
      });
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load chatbot admin");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveKb = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/chatbot/kb", { value: kb });
      toast.success(r.data.reset_to_default ? "Reset to default KB" : `Saved (${r.data.size} chars)`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const resetKb = async () => {
    if (!window.confirm("Reset the chatbot KB to the built-in default?")) return;
    setSaving(true);
    try {
      await api.put("/admin/chatbot/kb", { value: "" });
      toast.success("Reset to default");
      load();
    } catch (err) {
      toast.error("Reset failed");
    } finally { setSaving(false); }
  };

  const submitPromotion = async () => {
    const p = promoteState;
    if (!p) return;
    const q = p.question.trim();
    const a = p.answer.trim();
    if (q.length < 3 || a.length < 3) { toast.error("Question and answer are required"); return; }
    setPromoting(true);
    try {
      await api.post("/admin/faq", { category: p.category, question: q, answer: a });
      toast.success("Promoted to public FAQ");
      setPromoteState(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Promote failed");
    } finally { setPromoting(false); }
  };

  if (loading || !analytics) {
    return (
      <Card className="mt-6"><CardContent className="p-6">
        <div className="h-24 rounded-md bg-muted animate-pulse" />
      </CardContent></Card>
    );
  }

  const a = analytics;
  return (
    <>
    <Card className="mt-6" data-testid="chatbot-admin-analytics">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <MessageCircle className="w-4 h-4 text-secondary" />
          <h2 className="serif text-2xl">Chatbot · Analytics</h2>
          <Badge variant="outline" className="mono text-[10px] uppercase" data-testid="chatbot-sessions-total">
            {a.sessions_total} sessions all-time
          </Badge>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard testId="chatbot-vol-24h" icon={MessageCircle} label="Sessions 24h" value={fmt(a.sessions_volume["24h"])} />
          <StatCard testId="chatbot-vol-7d"  icon={MessageCircle} label="Sessions 7d"  value={fmt(a.sessions_volume["7d"])} />
          <StatCard testId="chatbot-vol-30d" icon={MessageCircle} label="Sessions 30d" value={fmt(a.sessions_volume["30d"])} accent="primary" />
          <StatCard testId="chatbot-escalation-rate" icon={AlertCircle} label="Escalation 30d" value={`${a.escalation_rate_30d}%`} accent="secondary" sub={`${a.message_totals_30d.escalated} of ${a.message_totals_30d.assistant} replies`} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Top escalation reasons · 30d</div>
            {a.top_escalation_reasons.length === 0 ? (
              <div className="text-sm text-muted-foreground italic">No escalations yet.</div>
            ) : (
              <div className="space-y-1.5" data-testid="chatbot-top-reasons">
                {a.top_escalation_reasons.map((r) => (
                  <div key={r.reason} className="flex items-center justify-between text-sm border-b border-border/40 py-1 last:border-b-0">
                    <span className="truncate">{r.reason}</span>
                    <Badge variant="outline" className="mono text-[10px]">{r.count}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Recent user questions · last 25</div>
            {a.recent_questions.length === 0 ? (
              <div className="text-sm text-muted-foreground italic">No questions yet.</div>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto pr-1" data-testid="chatbot-recent-questions">
                {a.recent_questions.map((q, i) => (
                  <div key={i} className="flex items-start gap-2 group border-b border-border/40 py-1 last:border-b-0">
                    <div className="flex-1 min-w-0 text-xs text-foreground/85 truncate" title={q.text}>
                      "{q.text}"
                    </div>
                    <button
                      type="button"
                      onClick={() => setPromoteState({ question: q.text, answer: "", category: "features" })}
                      className="mono text-[9px] uppercase tracking-widest text-primary hover:underline shrink-0"
                      data-testid={`chatbot-promote-${i}`}
                      title="Promote this question to a public FAQ"
                    >
                      + FAQ
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Recent sessions</div>
          <div className="space-y-1" data-testid="chatbot-recent-sessions">
            {a.recent_sessions.map((s) => (
              <div key={s.session_id} className="rounded-md border border-border">
                <button
                  type="button"
                  onClick={() => setOpenSessionId(openSessionId === s.session_id ? null : s.session_id)}
                  className="w-full text-left px-3 py-2 flex items-center gap-3 flex-wrap hover:bg-muted/30 transition-colors"
                  data-testid={`chatbot-session-toggle-${s.session_id}`}
                >
                  {s.escalated && <Badge variant="outline" className="mono text-[9px] uppercase text-secondary border-secondary/40">escalated</Badge>}
                  <span className="text-sm flex-1 min-w-0 truncate">{s.first_question || "(empty session)"}</span>
                  <span className="mono text-[10px] text-muted-foreground whitespace-nowrap">{s.turns} turn{s.turns === 1 ? "" : "s"} · {fmtDate(s.updated_at || s.created_at)}</span>
                </button>
                {openSessionId === s.session_id && (
                  <div className="px-3 pb-3 pt-1 border-t border-border/60 space-y-1.5" data-testid={`chatbot-session-body-${s.session_id}`}>
                    {s.messages.map((m, i) => (
                      <div key={i} className={`text-xs leading-relaxed ${m.role === "user" ? "text-foreground" : "text-muted-foreground pl-3"}`}>
                        <span className="mono text-[9px] uppercase tracking-widest mr-2">{m.role === "user" ? "USER" : "BOT"}</span>
                        {m.content}
                        {m.escalate && <Badge variant="outline" className="ml-2 mono text-[8px] uppercase text-secondary border-secondary/40">esc: {m.reason}</Badge>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="mt-4" data-testid="chatbot-admin-kb">
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-secondary" />
            <h2 className="serif text-2xl">Chatbot · Knowledge base</h2>
            <Badge variant="outline" className="mono text-[10px] uppercase">
              {kbMeta.is_default ? "using default" : "customized"}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={resetKb} disabled={saving || kbMeta.is_default} className="rounded-full" data-testid="chatbot-kb-reset">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset to default
            </Button>
            <Button size="sm" onClick={saveKb} disabled={saving || !dirty} className="rounded-full" data-testid="chatbot-kb-save">
              {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-3.5 h-3.5 mr-1.5" /> Save KB</>}
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-3">
          Everything below is fed to the chatbot as its ground truth. Add product details, edit pricing, or drop in FAQs — no deploy needed. The bot is told to answer ONLY from this KB, so keep it accurate. Max 20,000 characters.
        </p>
        <Textarea
          value={kb}
          onChange={(e) => { setKb(e.target.value); setDirty(true); }}
          rows={20}
          maxLength={20000}
          className="mono text-xs leading-relaxed"
          data-testid="chatbot-kb-textarea"
        />
        <div className="mono text-[10px] text-muted-foreground mt-2 flex items-center justify-between">
          <span>{kb.length}/20,000 chars</span>
          {kbMeta.updated_at && !kbMeta.is_default && (
            <span>Last edited {fmtDate(kbMeta.updated_at)}{kbMeta.updated_by ? ` by ${kbMeta.updated_by}` : ""}</span>
          )}
        </div>
      </CardContent>
    </Card>

    <Dialog open={!!promoteState} onOpenChange={(o) => { if (!o) setPromoteState(null); }}>
      <DialogContent className="max-w-lg" data-testid="chatbot-promote-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-xl">Promote to public FAQ</DialogTitle>
          <DialogDescription>
            Turn this common question into a permanent entry on <Link to="/faq" className="underline hover:text-primary">/faq</Link>. Users searching in-app or via Google will find it directly.
          </DialogDescription>
        </DialogHeader>
        {promoteState && (
          <div className="space-y-3 py-2">
            <div>
              <Label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Category</Label>
              <select
                value={promoteState.category}
                onChange={(e) => setPromoteState({ ...promoteState, category: e.target.value })}
                className="mt-1 w-full rounded-md border border-border bg-card px-3 h-9 text-sm"
                data-testid="chatbot-promote-category"
              >
                <option value="features">Features</option>
                <option value="value">Value & Pricing</option>
                <option value="enroll">Sign-up & Enroll</option>
              </select>
            </div>
            <div>
              <Label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Question</Label>
              <Input
                value={promoteState.question}
                onChange={(e) => setPromoteState({ ...promoteState, question: e.target.value })}
                className="mt-1 h-9 text-sm"
                maxLength={300}
                data-testid="chatbot-promote-question"
              />
            </div>
            <div>
              <Label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Answer</Label>
              <Textarea
                value={promoteState.answer}
                onChange={(e) => setPromoteState({ ...promoteState, answer: e.target.value })}
                rows={5}
                maxLength={4000}
                placeholder="Write the official answer users will see on /faq"
                className="mt-1 text-sm"
                data-testid="chatbot-promote-answer"
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => setPromoteState(null)} className="rounded-full">Cancel</Button>
          <Button onClick={submitPromotion} disabled={promoting} className="rounded-full" data-testid="chatbot-promote-submit">
            {promoting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Promoting…</> : "Publish to FAQ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}


// ---------- Team pricing (admin-only) ----------
// Reuses the TeamCard from Pricing.jsx — kept off the public pricing page
// (2026-02-13) so retail visitors see a cleaner Free · Core · Pro grid. The
// contact-sales lead flow still posts to /waitlist so admins can review
// enterprise leads under Contact inbox.
function TeamPricingAdminCard({ user }) {
  return (
    <Card className="mt-6" data-testid="admin-team-pricing-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 text-primary">
          <Building2 className="w-5 h-5" />
          <div>
            <div className="serif text-2xl">Team pricing</div>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Internal seat-based tier for advisors and desks. Public pricing page shows Free · Core · Pro only.
            </p>
          </div>
        </div>
        <div className="mt-6 max-w-md">
          <TeamCard user={user} />
        </div>
      </CardContent>
    </Card>
  );
}


// ---------- FAQ admin ----------
function FaqAdminCard() {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // id -> { question, answer, order, active }
  const [newEntry, setNewEntry] = useState({ category: "features", question: "", answer: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/faq");
      setEntries(r.data.entries || []);
      setCategories(r.data.categories || []);
      setDrafts({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load FAQs");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createOne = async () => {
    const q = newEntry.question.trim();
    const a = newEntry.answer.trim();
    if (q.length < 3 || a.length < 3) { toast.error("Question and answer are required"); return; }
    setSaving(true);
    try {
      await api.post("/admin/faq", { category: newEntry.category, question: q, answer: a, order: entries.filter((e) => e.category === newEntry.category).length });
      toast.success("FAQ added");
      setNewEntry({ category: newEntry.category, question: "", answer: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  const patchOne = async (id, updates) => {
    try {
      await api.patch(`/admin/faq/${id}`, updates);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    }
  };

  const saveDraft = async (id) => {
    const d = drafts[id];
    if (!d) return;
    await patchOne(id, d);
    toast.success("Saved");
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Delete this FAQ entry?")) return;
    try {
      await api.delete(`/admin/faq/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const grouped = categories.map((c) => ({
    ...c,
    entries: entries.filter((e) => e.category === c.key),
  }));

  return (
    <Card className="mt-6" data-testid="faq-admin-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle className="w-4 h-4 text-secondary" />
          <h2 className="serif text-2xl">Public FAQ</h2>
          <Badge variant="outline" className="mono text-[10px] uppercase" data-testid="faq-total-count">
            {entries.length} entries
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Live on <Link to="/faq" className="underline hover:text-primary">/faq</Link>. Deactivate to hide from the public page without deleting. Category order is fixed (Features · Value · Sign-up); within each, entries render in ascending <code className="mono">order</code>.
        </p>

        {/* New entry composer */}
        <div className="rounded-md border border-dashed border-primary/30 bg-primary/[0.03] p-4 mb-6" data-testid="faq-new-block">
          <div className="mono text-[10px] uppercase tracking-widest text-primary mb-2">Add new entry</div>
          <div className="grid gap-2">
            <div className="flex gap-2">
              <select
                value={newEntry.category}
                onChange={(e) => setNewEntry({ ...newEntry, category: e.target.value })}
                className="rounded-md border border-border bg-card px-3 text-sm h-9"
                data-testid="faq-new-category"
              >
                {categories.map((c) => <option key={c.key} value={c.key}>{c.title}</option>)}
              </select>
              <Input
                placeholder="Question"
                value={newEntry.question}
                onChange={(e) => setNewEntry({ ...newEntry, question: e.target.value })}
                className="h-9 text-sm flex-1"
                data-testid="faq-new-question"
              />
            </div>
            <Textarea
              placeholder="Answer (Markdown supported by chatbot, plain text on /faq)"
              value={newEntry.answer}
              onChange={(e) => setNewEntry({ ...newEntry, answer: e.target.value })}
              rows={3}
              className="text-sm"
              data-testid="faq-new-answer"
            />
            <div className="flex justify-end">
              <Button size="sm" onClick={createOne} disabled={saving} className="rounded-full" data-testid="faq-new-submit">
                {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Adding…</> : <><PlusCircleIcon className="w-3.5 h-3.5 mr-1.5" /> Add FAQ</>}
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="h-16 rounded-md bg-muted animate-pulse" />)}</div>
        ) : (
          <div className="space-y-6" data-testid="faq-admin-groups">
            {grouped.map((cat) => (
              <div key={cat.key} data-testid={`faq-admin-group-${cat.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="serif text-lg">{cat.title}</span>
                  <Badge variant="outline" className="mono text-[9px] uppercase">{cat.entries.length}</Badge>
                </div>
                {cat.entries.length === 0 ? (
                  <div className="text-xs text-muted-foreground italic pl-2">No entries yet.</div>
                ) : (
                  <div className="space-y-2">
                    {cat.entries.map((e) => {
                      const draft = drafts[e.id];
                      const isDirty = !!draft;
                      const q = draft?.question ?? e.question;
                      const a = draft?.answer ?? e.answer;
                      const active = draft?.active !== undefined ? draft.active : e.active;
                      const order = draft?.order !== undefined ? draft.order : e.order;
                      const setDraft = (patch) => setDrafts((d) => ({ ...d, [e.id]: { ...(d[e.id] || {}), ...patch } }));
                      return (
                        <div key={e.id} className="rounded-md border border-border p-3" data-testid={`faq-admin-row-${e.id}`}>
                          <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            <Input
                              value={q}
                              onChange={(ev) => setDraft({ question: ev.target.value })}
                              className="h-8 text-sm flex-1 min-w-[240px]"
                              data-testid={`faq-admin-q-${e.id}`}
                            />
                            <input
                              type="number"
                              value={order}
                              onChange={(ev) => setDraft({ order: parseInt(ev.target.value, 10) || 0 })}
                              className="w-14 h-8 rounded-md border border-border bg-card px-2 text-sm mono text-center"
                              title="Sort order within category"
                              data-testid={`faq-admin-order-${e.id}`}
                            />
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => patchOne(e.id, { active: !active })}
                              className="rounded-full text-xs"
                              data-testid={`faq-admin-toggle-${e.id}`}
                            >
                              {active ? "Deactivate" : "Activate"}
                            </Button>
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => deleteOne(e.id)}
                              className="rounded-full text-xs text-destructive hover:text-destructive"
                              data-testid={`faq-admin-delete-${e.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                          <Textarea
                            value={a}
                            onChange={(ev) => setDraft({ answer: ev.target.value })}
                            rows={3}
                            className="text-sm mono text-xs"
                            data-testid={`faq-admin-a-${e.id}`}
                          />
                          <div className="flex items-center justify-between mt-1.5">
                            <div className="mono text-[10px] text-muted-foreground">
                              {!active && <span className="text-secondary mr-2">hidden from /faq</span>}
                              Updated {fmtDate(e.updated_at)}
                            </div>
                            <Button
                              size="sm"
                              onClick={() => saveDraft(e.id)}
                              disabled={!isDirty}
                              className="rounded-full text-xs"
                              data-testid={`faq-admin-save-${e.id}`}
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Site announcements CRUD ----------
function AnnouncementsAdminCard() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [alsoPush, setAlsoPush] = useState(false);
  const [alsoEmail, setAlsoEmail] = useState(false);
  const [broadcastSegment, setBroadcastSegment] = useState("all");
  const [draft, setDraft] = useState({
    title: "", body: "", kind: "info", href: "", cta_label: "",
    starts_at: "", ends_at: "", is_active: true, dismissible: true,
  });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/announcements");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Failed to load announcements");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!draft.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      // Backend rejects empty strings for dates — send only when provided.
      const payload = { ...draft };
      if (!payload.starts_at) delete payload.starts_at;
      if (!payload.ends_at) delete payload.ends_at;
      if (!payload.href) delete payload.href;
      if (!payload.cta_label) delete payload.cta_label;
      if (!payload.body) delete payload.body;
      await api.post("/admin/announcements", payload);
      toast.success("Announcement published");

      // Optional companion broadcasts. Fired in parallel to keep the UI snappy.
      const jobs = [];
      if (alsoPush) {
        jobs.push(
          api.post("/admin/push-broadcast", {
            segment: broadcastSegment,
            title: draft.title,
            body: draft.body || draft.title,
            url: draft.href || "/",
          }).then(r => toast.success(`Push sent — ${r.data.users_notified} user(s), ${r.data.pushes_sent} device(s)`))
            .catch(err => toast.error(`Push failed: ${err.response?.data?.detail || err.message}`))
        );
      }
      if (alsoEmail) {
        const bodyText = draft.body || draft.title;
        const cta = draft.href ? `<p style="margin-top:16px"><a href="https://dividendportfolioplanner.com${draft.href.startsWith("/") ? draft.href : "/" + draft.href}" style="background:#1a4d2e;color:#fff;padding:10px 20px;text-decoration:none;border-radius:24px;display:inline-block">${draft.cta_label || "Read more"}</a></p>` : "";
        const html = `<div style="padding:24px 32px;font-family:'Helvetica Neue',Arial,sans-serif;color:#111"><h2 style="margin:0 0 12px;font-size:20px">${draft.title}</h2><p style="margin:0;line-height:1.55;color:#555">${bodyText}</p>${cta}</div>`;
        jobs.push(
          api.post("/admin/email-broadcast", {
            segment: broadcastSegment,
            subject: draft.title,
            html,
          }).then(r => toast.success(`Email sent — ${r.data.sent}/${r.data.eligible_recipients}`))
            .catch(err => toast.error(`Email failed: ${err.response?.data?.detail || err.message}`))
        );
      }
      if (jobs.length > 0) await Promise.all(jobs);

      setDraft({ title: "", body: "", kind: "info", href: "", cta_label: "", starts_at: "", ends_at: "", is_active: true, dismissible: true });
      setAlsoPush(false); setAlsoEmail(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/admin/announcements/${a.id}`, { is_active: !a.is_active });
      await load();
    } catch { toast.error("Failed to update"); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/admin/announcements/${a.id}`);
      toast.success("Deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <Card className="mt-6" data-testid="announcements-admin-card">
      <CardContent className="p-6 space-y-5">
        <div>
          <h2 className="serif text-2xl">Site announcements</h2>
          <p className="text-sm text-muted-foreground mt-1">Every active, non-expired banner renders above the nav on every page. Users can dismiss dismissible ones — the choice is remembered. Optionally amplify with push + email in the same submit.</p>
        </div>

        <form onSubmit={create} className="rounded-md border border-border p-4 space-y-3" data-testid="announcement-create-form">
          <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-3">
            <div>
              <Label>Title</Label>
              <Input data-testid="ann-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="e.g. SnapTrade broker sync is live!" />
            </div>
            <div>
              <Label>Kind</Label>
              <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
                <option value="info">Info (muted)</option>
                <option value="success">Success (green)</option>
                <option value="warning">Warning (amber)</option>
                <option value="maintenance">Maintenance (red)</option>
              </select>
            </div>
          </div>
          <div>
            <Label>Body (optional)</Label>
            <Textarea rows={2} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} placeholder="Short subtitle shown to the right of the title." />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>CTA URL (optional)</Label>
              <Input data-testid="ann-href" value={draft.href} onChange={(e) => setDraft({ ...draft, href: e.target.value })} placeholder="/holdings" />
            </div>
            <div>
              <Label>CTA label (optional)</Label>
              <Input value={draft.cta_label} onChange={(e) => setDraft({ ...draft, cta_label: e.target.value })} placeholder="Try it" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Starts at (optional — default: now)</Label>
              <Input type="datetime-local" value={draft.starts_at} onChange={(e) => setDraft({ ...draft, starts_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} />
            </div>
            <div>
              <Label>Ends at (optional — default: never)</Label>
              <Input type="datetime-local" value={draft.ends_at} onChange={(e) => setDraft({ ...draft, ends_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="text-sm inline-flex items-center gap-2">
              <Checkbox checked={draft.is_active} onCheckedChange={(v) => setDraft({ ...draft, is_active: !!v })} />
              Active
            </label>
            <label className="text-sm inline-flex items-center gap-2">
              <Checkbox checked={draft.dismissible} onCheckedChange={(v) => setDraft({ ...draft, dismissible: !!v })} />
              Dismissible
            </label>
          </div>

          {/* Also send as push / email — the whole point of this refactor */}
          <div className="rounded-md border border-primary/20 bg-primary/[0.03] p-3 space-y-2">
            <div className="mono text-[10px] uppercase tracking-widest text-primary">Amplify (optional)</div>
            <div className="flex items-center gap-4 flex-wrap">
              <label className="text-sm inline-flex items-center gap-2">
                <Checkbox checked={alsoPush} onCheckedChange={(v) => setAlsoPush(!!v)} data-testid="ann-also-push" />
                <Radio className="w-3.5 h-3.5" /> Also send as push notification
              </label>
              <label className="text-sm inline-flex items-center gap-2">
                <Checkbox checked={alsoEmail} onCheckedChange={(v) => setAlsoEmail(!!v)} data-testid="ann-also-email" />
                <Mail className="w-3.5 h-3.5" /> Also send as email
              </label>
              {(alsoPush || alsoEmail) && (
                <div className="inline-flex items-center gap-2">
                  <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Segment</span>
                  <select className="h-8 px-2 rounded-md border border-input bg-background text-xs" value={broadcastSegment} onChange={(e) => setBroadcastSegment(e.target.value)} data-testid="ann-broadcast-segment">
                    <option value="all">All users</option>
                    <option value="pro">Pro only</option>
                    <option value="free">Free only</option>
                  </select>
                </div>
              )}
            </div>
            {(alsoPush || alsoEmail) && (
              <div className="text-[11px] text-muted-foreground">
                Push respects browser permission grants only. Email respects newsletter opt-outs. Both fire immediately on Publish.
              </div>
            )}
          </div>

          <Button type="submit" disabled={busy} data-testid="ann-submit" className="rounded-full">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing…</> : <><PlusCircleIcon className="w-4 h-4 mr-2" /> Publish</>}
          </Button>
        </form>

        <div className="space-y-2">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Existing ({items.length})</div>
          {items.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-md">No announcements yet</div>}
          {items.map((a) => (
            <div key={a.id} className="border border-border rounded-md p-3 flex items-start gap-3" data-testid={`ann-row-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{a.title}</span>
                  <Badge variant="outline" className="mono text-[9px] uppercase">{a.kind}</Badge>
                  {!a.is_active && <Badge variant="outline" className="mono text-[9px] uppercase text-muted-foreground">Inactive</Badge>}
                  {a.dismissible === false && <Badge variant="outline" className="mono text-[9px] uppercase">Persistent</Badge>}
                </div>
                {a.body && <div className="text-sm text-foreground/80 mt-0.5">{a.body}</div>}
                <div className="mono text-[10px] text-muted-foreground mt-1">
                  {fmtDate(a.starts_at)} → {a.ends_at ? fmtDate(a.ends_at) : "no end"} · by {a.created_by}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => toggleActive(a)} data-testid={`ann-toggle-${a.id}`}>
                  {a.is_active ? "Pause" : "Resume"}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove(a)} data-testid={`ann-delete-${a.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- Push broadcast (segment-targeted Web Push) ----------
function PushBroadcastCard() {
  const [segment, setSegment] = useState("all");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/");
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) { toast.error("Title + body required"); return; }
    const emailList = segment === "email_list"
      ? emails.split(/[\s,;\n]+/).map(s => s.trim()).filter(s => s.includes("@"))
      : undefined;
    if (segment === "email_list" && (!emailList || emailList.length === 0)) {
      toast.error("Paste at least one email address"); return;
    }
    if (!window.confirm(`Send this push to "${segment}"${emailList ? ` (${emailList.length} emails)` : ""}?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/push-broadcast", { segment, title, body, url, emails: emailList });
      toast.success(`Sent — ${r.data.users_notified} user(s), ${r.data.pushes_sent} device(s). ${r.data.pushes_failed} failed.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Broadcast failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mt-6" data-testid="push-broadcast-card">
      <CardContent className="p-6">
        <h2 className="serif text-2xl">Push broadcast</h2>
        <p className="text-sm text-muted-foreground mt-1">Send a Web Push notification to a segment. Users only receive it if they've installed the PWA and granted permission.</p>
        <form onSubmit={send} className="mt-4 space-y-3">
          <div>
            <Label>Segment</Label>
            <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={segment} onChange={(e) => setSegment(e.target.value)} data-testid="push-segment">
              <option value="all">All subscribed users</option>
              <option value="pro">Pro subscribers only</option>
              <option value="free">Free users only</option>
              <option value="email_list">Custom email list…</option>
            </select>
          </div>
          {segment === "email_list" && (
            <div>
              <Label>Emails (paste one per line, comma, or space)</Label>
              <Textarea rows={5} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="alice@example.com&#10;bob@example.com&#10;carol@example.com" data-testid="push-emails" className="mono text-xs" />
              <div className="mono text-[10px] text-muted-foreground mt-1">Emails not in the users table are silently skipped so we don't leak membership.</div>
            </div>
          )}
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. New: AI Income Target Planner" data-testid="push-title" />
          </div>
          <div>
            <Label>Body</Label>
            <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="e.g. Solve for a $2,000/mo dividend paycheck with one click." data-testid="push-body" />
          </div>
          <div>
            <Label>Click URL</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="/dashboard" data-testid="push-url" />
          </div>
          <Button type="submit" disabled={busy} data-testid="push-send" className="rounded-full">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send push</>}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------- Integration health dashboard ----------
const HEALTH_COLOR = {
  ok: "bg-primary/15 text-primary border-primary/40",
  down: "bg-destructive/15 text-destructive border-destructive/40",
  not_configured: "bg-muted text-muted-foreground border-border",
};
function HealthDashboardCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/health");
      setData(r.data);
    } catch { toast.error("Failed to load health"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Card className="mt-6" data-testid="health-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="serif text-2xl">Integration health</h2>
            <p className="text-sm text-muted-foreground">Snapshot of every 3rd-party integration + DB. Cheap check — env-presence and heartbeat freshness, no external pings.</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="health-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
        {!data && <div className="text-sm text-muted-foreground text-center py-8">Loading…</div>}
        {data && (
          <div className="grid sm:grid-cols-2 gap-3">
            {data.integrations.map((it) => (
              <div key={it.id} className={`rounded-md border p-3 ${HEALTH_COLOR[it.status] || HEALTH_COLOR.not_configured}`} data-testid={`health-${it.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium">{it.name}</div>
                  <Badge variant="outline" className="mono text-[9px] uppercase bg-background">
                    {it.status === "ok" ? "OK" : it.status === "down" ? "DOWN" : "N/C"}
                  </Badge>
                </div>
                <div className="text-xs mt-1 opacity-80">{it.detail}</div>
              </div>
            ))}
          </div>
        )}
        {data && (
          <div className="mono text-[10px] text-muted-foreground mt-4">Generated at {fmtDate(data.generated_at)}</div>
        )}
      </CardContent>
    </Card>
  );
}



// ---------- Feature flags admin ----------
// Curated list of known/upcoming feature flag keys — the create form shows
// this as a dropdown so admins pick from a well-defined vocabulary instead of
// typing free-text keys that drift over time. Extend this array as new
// beta features come online; the list drives both the dropdown AND the
// "description" auto-fill so admins don't have to remember what a key guards.
const FLAG_KEY_CATALOG = [
  { key: "ai_optimizer_v2",         description: "Beta: rewritten AI Portfolio Optimizer with income-safety tradeoff sliders" },
  { key: "income_target_v2",        description: "Beta: reverse-solve Income Target Planner with tax-adjusted YoC + drawdown modeling" },
  { key: "research_chat_v2",        description: "Beta: multi-turn Research Chat with real-time SnapTrade transaction context" },
  { key: "compare_scenarios_v2",    description: "Beta: 3+ portfolio side-by-side comparison with drawdown overlays" },
  { key: "dividends_ytd_widget",    description: "Show the Dividends Received YTD tile in the Holdings rollup (requires a SnapTrade Sync history run)" },
  { key: "personalized_ticker_tape",description: "Personalize the top ticker tape with the user's own holdings first" },
  { key: "dark_mode",               description: "Beta: dark theme toggle in the user avatar dropdown" },
  { key: "csv_bulk_import_v2",      description: "Beta: paste any broker CSV — DPP infers columns automatically" },
  { key: "chatbot_dividend_skill",  description: "Route dividend-history questions to the SnapTrade transactions cache instead of the LLM" },
  { key: "team_seats",              description: "Beta: multi-seat team billing + shared portfolios" },
  { key: "sms_alerts",              description: "Beta: ex-dividend SMS alerts via Twilio (in addition to push)" },
  { key: "portfolio_public_share",  description: "Beta: shareable read-only portfolio URLs" },
];

function FeatureFlagsAdminCard() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [expandedKey, setExpandedKey] = useState(null);
  const [grantEmailByKey, setGrantEmailByKey] = useState({});

  const load = useCallback(async () => {
    try {
      const [flags, u] = await Promise.all([
        api.get("/admin/feature-flags"),
        api.get("/admin/users?limit=200"),
      ]);
      setItems(flags.data.items || []);
      setUsers(u.data || []);
    } catch { toast.error("Failed to load flags"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // When the admin picks a catalog key, pre-fill the description so they can
  // still tweak it before hitting Create.
  useEffect(() => {
    if (!newKey) return;
    const entry = FLAG_KEY_CATALOG.find((f) => f.key === newKey);
    if (entry && !newDesc.trim()) setNewDesc(entry.description);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newKey]);

  const existingKeys = new Set(items.map((i) => i.key));
  const availableCatalog = FLAG_KEY_CATALOG.filter((f) => !existingKeys.has(f.key));

  const create = async (e) => {
    e.preventDefault();
    if (!newKey.trim()) { toast.error("Pick a key"); return; }
    setBusy(true);
    try {
      await api.post("/admin/feature-flags", { key: newKey.trim(), description: newDesc.trim(), enabled_globally: false });
      toast.success(`Flag "${newKey}" created`);
      setNewKey(""); setNewDesc("");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  const toggleGlobal = async (f) => {
    try {
      await api.patch(`/admin/feature-flags/${f.key}`, { enabled_globally: !f.enabled_globally });
      await load();
    } catch { toast.error("Toggle failed"); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Delete flag "${f.key}"? Frontend gates will silently start returning false.`)) return;
    try {
      await api.delete(`/admin/feature-flags/${f.key}`);
      toast.success("Deleted");
      await load();
    } catch { toast.error("Delete failed"); }
  };
  const grant = async (f) => {
    const email = (grantEmailByKey[f.key] || "").trim().toLowerCase();
    if (!email) { toast.error("Type an email first"); return; }
    const target = users.find(u => u.email.toLowerCase() === email);
    if (!target) { toast.error(`No user with email ${email}`); return; }
    try {
      await api.post(`/admin/feature-flags/${f.key}/grant/${target.id}`);
      toast.success(`Granted "${f.key}" to ${email}`);
      setGrantEmailByKey({ ...grantEmailByKey, [f.key]: "" });
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Grant failed"); }
  };
  const revoke = async (f, uid) => {
    try {
      await api.delete(`/admin/feature-flags/${f.key}/grant/${uid}`);
      await load();
    } catch { toast.error("Revoke failed"); }
  };

  return (
    <Card className="mt-6" data-testid="feature-flags-admin-card">
      <CardContent className="p-6 space-y-5">
        <div>
          <h2 className="serif text-2xl">Feature flags</h2>
          <p className="text-sm text-muted-foreground mt-1">Toggle beta features globally, or grant them to specific users. Frontend components check via <code className="mono text-xs">useFeatureFlags().has("key")</code>.</p>
        </div>

        <form onSubmit={create} className="rounded-md border border-border p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-end">
            <div>
              <Label>Key</Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm mono"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
                data-testid="flag-new-key"
              >
                <option value="">— pick a flag key —</option>
                {availableCatalog.map((f) => (
                  <option key={f.key} value={f.key}>{f.key}</option>
                ))}
                {availableCatalog.length === 0 && <option disabled>All catalog flags already created</option>}
              </select>
            </div>
            <div>
              <Label>Description (auto-fills; edit as needed)</Label>
              <Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Short reminder of what this flag guards" data-testid="flag-new-desc" />
            </div>
            <Button type="submit" disabled={busy || !newKey} data-testid="flag-create" className="rounded-full">
              <PlusCircleIcon className="w-4 h-4 mr-2" /> Create
            </Button>
          </div>
        </form>

        <div className="space-y-2">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Flags ({items.length})</div>
          {items.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-md">No flags yet — create one above</div>}
          {items.map((f) => {
            const expanded = expandedKey === f.key;
            const grantedUsers = (f.granted_user_ids || []).map(id => users.find(u => u.id === id)).filter(Boolean);
            return (
              <div key={f.key} className="border border-border rounded-md" data-testid={`flag-row-${f.key}`}>
                <div className="p-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="mono text-sm font-semibold text-primary">{f.key}</code>
                      {f.enabled_globally && <Badge className="mono text-[9px] uppercase bg-primary text-primary-foreground">Global</Badge>}
                      {!f.enabled_globally && <Badge variant="outline" className="mono text-[9px] uppercase">Beta · {f.granted_users_count} user(s)</Badge>}
                    </div>
                    {f.description && <div className="text-sm text-foreground/80 mt-0.5">{f.description}</div>}
                    <div className="mono text-[10px] text-muted-foreground mt-1">by {f.created_by} · {fmtDate(f.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => toggleGlobal(f)} data-testid={`flag-toggle-${f.key}`}>
                      {f.enabled_globally ? "Turn off (global)" : "Turn on (global)"}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setExpandedKey(expanded ? null : f.key)}>
                      {expanded ? "Hide users" : `Beta users${f.granted_users_count ? ` (${f.granted_users_count})` : ""}`}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(f)} data-testid={`flag-delete-${f.key}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                {expanded && (
                  <div className="border-t border-border p-3 bg-muted/30 space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="user@example.com to grant"
                        value={grantEmailByKey[f.key] || ""}
                        onChange={(e) => setGrantEmailByKey({ ...grantEmailByKey, [f.key]: e.target.value })}
                        className="max-w-sm"
                        data-testid={`flag-grant-input-${f.key}`}
                      />
                      <Button size="sm" onClick={() => grant(f)} data-testid={`flag-grant-btn-${f.key}`} className="rounded-full">
                        <PlusCircleIcon className="w-3.5 h-3.5 mr-1" /> Grant
                      </Button>
                    </div>
                    {grantedUsers.length === 0 && <div className="text-xs text-muted-foreground">No beta users yet</div>}
                    {grantedUsers.map(u => (
                      <div key={u.id} className="flex items-center justify-between gap-2 text-xs">
                        <span className="mono">{u.email}</span>
                        <Button variant="ghost" size="sm" onClick={() => revoke(f, u.id)} data-testid={`flag-revoke-${f.key}-${u.id}`}>
                          <X className="w-3 h-3 mr-1" /> Revoke
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}


// ---------- SnapTrade Diagnostics (admin) ----------
// One-click live self-test of the SnapTrade brokerage-sync integration on the
// currently-deployed backend. Shows env-var presence (WITHOUT leaking secret
// values — only booleans + lengths) and hits SnapTrade's `/api-status` endpoint
// so any credential/regional/DNS issue surfaces here instead of showing up
// as a failed "Connect brokerage" click for the user.
function SnapTradeDiagnosticsCard() {
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  const run = async () => {
    setBusy(true); setErr(""); setData(null);
    try {
      const res = await api.get("/snaptrade/diagnostics");
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Request failed");
    } finally { setBusy(false); }
  };

  React.useEffect(() => { run(); }, []); // auto-run on open

  return (
    <Card>
      <CardContent className="p-6 space-y-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-secondary">Diagnostics</div>
            <h3 className="serif text-2xl mt-1">SnapTrade — live production self-test</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Runs against the currently-deployed backend pod. Confirms which
              SnapTrade env vars are actually loaded (WITHOUT leaking secret
              values — only presence + lengths) and hits <code>/api-status</code>
              live so you know if brokerage sync is up before a user tries to
              connect.
            </p>
          </div>
          <Button onClick={run} disabled={busy} data-testid="snaptrade-diag-run" className="rounded-full">
            {busy ? "Testing…" : "Run test"}
          </Button>
        </div>

        {err && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive" data-testid="snaptrade-diag-error">
            {err}
          </div>
        )}

        {data && (
          <div className="space-y-4" data-testid="snaptrade-diag-result">
            {/* Env-var presence grid */}
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Config on this pod</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <DiagRow label="SNAPTRADE_CLIENT_ID" ok={data.client_id_present} value={data.client_id_present ? `set · ${data.client_id_len} chars` : "MISSING"} />
                <DiagRow label="SNAPTRADE_CONSUMER_KEY" ok={data.consumer_key_present} value={data.consumer_key_present ? `set · ${data.consumer_key_len} chars` : "MISSING"} />
                <DiagRow label="Fernet encryption key" ok={data.encryption_key_present} value={data.encryption_key_present ? "loaded (reuses PLAID_TOKEN_ENC_KEY)" : "MISSING — set PLAID_TOKEN_ENC_KEY or SNAPTRADE_TOKEN_ENC_KEY"} />
                <DiagRow label="SDK version" ok={!!data.sdk_version} value={data.sdk_version || "unknown"} mono />
              </div>
            </div>

            {/* Live SnapTrade API call result */}
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Live SnapTrade API call</div>
              {data.api_status_test?.ok ? (
                <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm" data-testid="snaptrade-diag-live-ok">
                  <div className="font-medium text-emerald-700">/api-status — OK</div>
                  <div className="text-muted-foreground mt-1 mono text-xs">
                    online: <b>{String(data.api_status_test.online)}</b> · version: <b>{data.api_status_test.version}</b>
                  </div>
                </div>
              ) : (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm" data-testid="snaptrade-diag-live-fail">
                  <div className="font-medium text-destructive">/api-status — FAILED</div>
                  <div className="mt-1 mono text-xs break-words whitespace-pre-wrap text-foreground/85">
                    {data.api_status_test?.error || "unknown error"}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2 leading-relaxed">
                    <strong>Common causes:</strong>{" "}
                    <em>401 Unauthorized</em> = wrong CLIENT_ID or CONSUMER_KEY (regenerate in the SnapTrade dashboard);{" "}
                    <em>403 Forbidden</em> = using Personal-tier keys against a Commercial-only endpoint (get Commercial keys);{" "}
                    <em>Timeout / ConnectionError</em> = SnapTrade slow or outbound egress blocked from this pod;{" "}
                    <em>SDK version mismatch</em> = upgrade <code>snaptrade-python-sdk</code> in requirements.txt.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DiagRow({ label, ok, value, mono }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-border bg-card px-3 py-2">
      <span className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${ok ? "bg-emerald-500" : "bg-destructive"}`} />
      <div className="min-w-0 flex-1">
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className={`text-sm break-all ${mono ? "mono text-xs" : ""}`}>{value}</div>
      </div>
    </div>
  );
}
